import {
  EVENT_TYPE,
  SUBTYPE,
  approvalDecision,
  elicitationResponse,
  recentTurnsRequest,
  interrupt,
  modeChange,
  PAIR_KIND,
  parsePairingPayload,
  prompt,
  projectListRequest,
  deviceMonitorStart,
  deviceMonitorStop,
  DEVICE_CAPABILITY,
  DEVICE_UTILITY_CODES,
  clipboardRead,
  clipboardWrite,
  clipboardResult,
  clipboardTextError,
  keepAwakeStart,
  keepAwakeStop,
  keepAwakeStatusRequest,
  keepAwakeStatus,
  lifecycleCancel as lifecycleCancelMessage,
  normalizeKeepAwakeDuration,
  openSession as openSessionMessage,
  stateRequest,
  spawnSession as spawnSessionMessage,
  sessionListRequest,
  resumeSession as resumeSessionMessage,
  takeoverConfirm as takeoverConfirmMessage,
  forgetDevice as forgetDeviceMessage,
  sessionClaimed as sessionClaimedMessage,
  voiceMode,
  invokeCommand,
  RECENT_TURNS_DEFAULT,
} from '@aasis21/weft-shared';
import type {
  EventEnvelope,
  LaunchStatusMsg,
  LifecycleStatusMsg,
  PairingPayload,
  ProjectListMsg,
  PromptAttachment,
  PromptDelivery,
  DeviceSnapshotMsg,
  DeviceUtilityCode,
  SessionListMsg,
  SessionMode,
  SessionOffersMsg,
  SpawnMode,
  SpawnPairingMsg,
  SpawnResultMsg,
  StateSnapshotMsg,
} from '@aasis21/weft-shared';
import {
  connectDevice as connectDeviceSession,
  connectSession,
  createPhonePairingIdentity,
  pairSession,
  getStableDeviceId,
  getSenderName,
} from '@/lib/weftClient';
import type { PhonePairingIdentity, WeftClient } from '@/lib/weftClient';
import {
  loadLastActiveSessionId,
  loadSessions,
  patchSession,
  removeSession,
  setLastActiveSessionId,
  upsertSession,
} from '@/lib/sessions';
import {
  loadDevices,
  patchDevice,
  reconcileDeviceId,
  removeDevice,
  setDefaultDevice as persistDefaultDevice,
  upsertDevice,
  type RegisteredDevice,
  type DeviceHealthCache,
} from '@/lib/devices';
import { creativeName } from '@/lib/sessionNames';
import type { StoredSession } from '@/lib/sessions';
import type { StoredPairing } from '@/lib/storage';
import {
  loadPendingOperations,
  removePendingOperation,
  upsertPendingOperation,
  type PendingOperation,
  type PendingOperationStage,
} from '@/lib/pendingOperations';
import {
  allowTranscriptWrites,
  clearTranscript,
  discardTranscriptWrites,
  loadTranscript,
  saveTranscript,
} from '@/lib/transcripts';
import { clearEventLog, loadEventLog, saveEventLog, toDebugEvent } from '@/lib/eventLog';
import { sweepStorage } from '@/lib/storageJanitor';
import { restoreTimeline, toPersisted } from '@/lib/timeline';
import type { TimelineState } from '@/lib/timeline';
import { startDemoSession } from '@/lib/demoSimulator';
import type { DemoSession } from '@/lib/demoSimulator';
import {
  ensureNotificationPermission,
  notifyApprovalRequest,
  notifyElicitationRequest,
  notifySessionEnded,
} from '@/lib/notifications';
import { App } from '@capacitor/app';

import { makeStore, type AppStore, type RuntimeDeps } from '@/app/store';
import type { TransportRegistry } from '@/services/transport/registry';
import { emptySession, type ListenerDeviceState, type Session, type SessionMeta } from '@/session/model';
import {
  approvalDismissed,
  approvalRestored,
  busySet,
  coldSet,
  debugAppended,
  deviceDefaultSet,
  deviceErrorSet,
  deviceSeen,
  deviceEventAppended,
  deviceLastProjectSet,
  deviceProjectsLoadingSet,
  deviceProjectsReceived,
  deviceClipboardOpened,
  deviceClipboardCleared,
  deviceClipboardStarted,
  deviceClipboardResultReceived,
  deviceKeepAwakeStarted,
  deviceKeepAwakeFailed,
  deviceKeepAwakeStatusReceived,
  deviceUtilitiesCleared,
  deviceMonitoringFailed,
  deviceMonitoringStarted,
  deviceMonitoringStopped,
  deviceHealthCached,
  deviceSnapshotReceived,
  deviceSessionOffersReceived,
  deviceSessionsReceived,
  deviceSessionsLoadingSet,
  deviceOfferRemoved,
  deviceReconciled,
  deviceRemoved,
  devicesHydrated,
  deviceUpserted,
  elicitationDismissed,
  elicitationRestored,
  endedSet,
  envelopeReceived,
  heartbeatSet,
  historyLoadingSet,
  interruptRequested,
  lastEventAtSet,
  metaScannedAtSet,
  modeSet,
  noticeAppended,
  pinnedSet,
  promptFailed,
  readySet,
  sessionActivated,
  sessionAdded,
  sessionReconciled,
  spawnSlowSet,
  spawnStateSet,
  sessionRemoved,
  settlingSet,
  statusSet,
  titleSet,
  unreadSet,
  userPromptAppended,
} from '@/session/sessionsSlice';
import { makeUserItem } from '@/session/reducers/applyEnvelope';
import { optimistic } from '@/session/intents/optimistic';
import { selectAllSessions, selectManagerSnapshot, toTimelineState } from '@/session/selectors';
import { TerminalController, isTerminalEnvelope } from './terminalController';

// --- liveness / persistence constants (identical to the pre-refactor god-object) -----------------
const IDLE_AFTER_MS = 20_000;
const OFFLINE_AFTER_MS = 30_000;
const HOST_CONFIRM_MS = 30_000;
/** How often {@link SessionManager.reconnectAndConfirm} re-reads the card while waiting for the
 *  laptop to confirm. Coarse on purpose — nothing here is racing, and the wait is bounded by
 *  HOST_CONFIRM_MS either way. */
const RECONNECT_POLL_MS = 250;
const SESSION_RECONNECT_BASE_MS = 15_000;
const SESSION_RECONNECT_MAX_MS = 5 * 60_000;
// Device (listener) channel heartbeat cadence is 2min (extension/src/listener.mjs). Rule: allow the
// mobile-side offline threshold to be 50% longer than that cadence (3min) so one dropped beat
// doesn't flap the Online dot, without waiting too long to notice a real disconnect.
const DEVICE_OFFLINE_AFTER_MS = 180_000;
const DEVICE_UTILITY_TIMEOUT_MS = 10_000;
/** Base self-heal reconnect interval for an offline device in the watchdog, so a dead devtunnel/relay
 *  socket (which never re-opens on its own) gets re-established without an app foreground/restart, yet
 *  a genuinely-unreachable relay isn't hammered every tick. Matches the connect timeout (weftClient
 *  CONNECT_TIMEOUT_MS) so a failed attempt fully settles before retry. This is the FLOOR of the
 *  backoff — see deviceReconnectBackoffMs, which grows it with attempts + staleness. */
const DEVICE_RECONNECT_BASE_MS = 15_000;
/** Ceiling for the self-heal reconnect backoff — a device dark for a long time is almost certainly
 *  a laptop that's off, so probe it at most this often (still instantly retried on app foreground /
 *  boot, which resets the ramp — see handleResume()/init()). */
const DEVICE_RECONNECT_MAX_MS = 15 * 60_000;

/**
 * Self-heal reconnect backoff for an offline device, growing with BOTH the number of consecutive
 * failed attempts (exponential) and how long since the device was last seen. Rationale: a laptop
 * that just blipped (seen seconds ago, few attempts) should recover fast, but one dark for an hour
 * shouldn't have a socket thrown at it every 15s. The attempt counter is reset to 0 (instant retry)
 * on app foreground / boot, so a returning user always gets an immediate reconnect regardless of how
 * relaxed the background ramp had become. Capped at DEVICE_RECONNECT_MAX_MS.
 */
function deviceReconnectBackoffMs(attempts: number, sinceLastSeenMs: number): number {
  const exponential = DEVICE_RECONNECT_BASE_MS * 2 ** Math.min(attempts, 6);
  const stalenessFloor =
    sinceLastSeenMs > 2 * 60 * 60_000
      ? 15 * 60_000
      : sinceLastSeenMs > 30 * 60_000
        ? 5 * 60_000
        : sinceLastSeenMs > 5 * 60_000
          ? 60_000
          : DEVICE_RECONNECT_BASE_MS;
  return Math.min(Math.max(exponential, stalenessFloor), DEVICE_RECONNECT_MAX_MS);
}

function sessionReconnectBackoffMs(attempts: number): number {
  return Math.min(SESSION_RECONNECT_BASE_MS * 2 ** Math.min(attempts, 5), SESSION_RECONNECT_MAX_MS);
}
/** Fail-safe window after a `project_list_request` is sent: while a request is outstanding, extra
 *  refreshProjects triggers (boot auto-reconnect + watchdog self-heal + attachListener's trailing
 *  refresh + a manual pull all firing within seconds of a reconnect) are collapsed into the one
 *  in-flight request instead of each sending a duplicate. If the reply is dropped, the marker
 *  auto-clears after this window so refreshes can't wedge forever. */
const PROJECT_LIST_INFLIGHT_MS = 8_000;
const DEVICE_MONITOR_CAPABILITY = 'device-monitor-v1';
const DEVICE_MONITOR_INTERVAL_MS = 10_000;
const DEVICE_MONITOR_LEASE_MS = 45_000;
const DEVICE_MONITOR_RENEW_MS = 30_000;
const DEVICE_HEALTH_CACHE_WRITE_MS = 60_000;
/** Fail-safe window after a `session_list_request` is sent: if the async SESSION_LIST reply never
 *  arrives (wedged laptop), the "Resume a session" loading flag auto-clears after this window so the
 *  Load/Refresh button re-enables instead of spinning forever. */
const SESSION_LIST_INFLIGHT_MS = 8_000;
const INITIAL_HISTORY_GRACE_MS = 700;
const RESUME_DEBOUNCE_MS = 500;
const MAX_WARM_SESSIONS = 50;
/** Hard cap on registered listener devices (#186 reconnect fix follow-up): unlike sessions, whose
 *  warm pool is just an LRU of how many stay actively connected, EVERY registered device attempts
 *  to reconnect on boot/resume (see init()/handleResume()) — an unbounded list would mean an
 *  unbounded number of live listener sockets. Registering an 11th device is refused outright
 *  (registerListenerFromQr) rather than silently evicting the least-recently-seen one, since
 *  forgetting a laptop is a deliberate, named action the user should take themselves. */
const MAX_DEVICES = 10;
const META_PERSIST_THROTTLE_MS = 1_500;
const PERSIST_THROTTLE_MS = 800;
const LIVENESS_PERSIST_THROTTLE_MS = 30_000;
// #163 session lifecycle: after AUTO_ARCHIVE_MS of witnessed silence (subscribed but no heartbeat)
// a session is cooled down (Archived); after AUTO_DELETE_MS of witnessed silence it is purged.
const AUTO_ARCHIVE_MS = 6 * 60 * 60 * 1_000; // 6 hours
const AUTO_DELETE_MS = 2 * 24 * 60 * 60 * 1_000; // 2 days
// Witnessed silence only accrues while the app is actually running, so a phone that was closed for a
// week comes back with a session that looks freshly-seen — subscribed and beaten within moments of
// each other — and stays Live/Offline in the list next to a twelve-day-old last message. This is the
// second, additive archive rule: judge BOTH clocks against `now` rather than against each other, and
// archive only when both are stale. Either one alone would be wrong. Heartbeat-alone would archive a
// laptop closed for the weekend that the phone never got the chance to check on; subscribed-alone
// would archive sessions the phone simply hasn't looked at yet.
const AUTO_ARCHIVE_STALE_MS = 2 * 24 * 60 * 60 * 1_000; // 2 days
const HISTORY_REQUEST_TIMEOUT_MS = 8_000;
const SPAWN_DELIVERY_TIMEOUT_MS = 30_000;
const PAIRING_ATTEMPT_TIMEOUT_MS = 20_000;
const STALE_LAUNCH_MS = 5 * 60_000;
/* Resume is not spawn. `copilot --resume` has to open the session store, find the record and replay
   it before the listener can pair the channel, and on a store holding a couple of thousand sessions
   a cold Windows laptop routinely takes longer than a fresh spawn's half-minute. Failing at 30s was
   declaring a session dead that was still perfectly on its way — so resume gets its own, longer
   fuse, and says so on screen while it waits. */
const RESUME_TIMEOUT_MS = 90_000;
/* How long a resume may run before the placeholder admits it is taking a while. Silence past the
   point a spawn would have finished reads as a hang. */
const RESUME_SLOW_MS = 25_000;

type ManagerSnapshot = ReturnType<typeof selectManagerSnapshot>;

interface SpawnRequestOptions {
  projectName: string;
  mode: SpawnMode;
  name?: string;
  operationId?: string;
}

interface ResumeRequestOptions {
  sessionId: string;
  storeAuthority?: string | null;
  mode: SpawnMode;
  /** The tapped store session's title/cwd, used purely to label the Initializing card until the
   *  resumed CLI session re-reports its own title/cwd via session_meta once paired. */
  title?: string | null;
  cwd?: string | null;
  /** Close a session already attached on the laptop and resume anyway. Set only on the user's
   *  second, confirmed tap — see {@link resumeSessionMessage}. */
  force?: boolean;
  operationId?: string;
}

interface PendingSpawn extends PendingOperation {
  timer?: ReturnType<typeof setTimeout>;
  pairingInFlight?: boolean;
  /** Fires partway through a resume to say it is still working, so a long wait doesn't read as a
   *  hang. Cleared alongside `timer`. */
  slowTimer?: ReturnType<typeof setTimeout>;
}

interface LifecycleWaiter {
  afterRevision: number;
  resolve(status: LifecycleStatusMsg): void;
  reject(error: Error): void;
  timer: ReturnType<typeof setTimeout>;
}

interface DeviceMonitorLease {
  monitorId: string;
  expiresAt: number;
  renewal: ReturnType<typeof setInterval>;
}

function clearSpawnTimers(pending: PendingSpawn): void {
  if (pending.timer) clearTimeout(pending.timer);
  if (pending.slowTimer) clearTimeout(pending.slowTimer);
}

function persistedPending(pending: PendingSpawn): PendingOperation {
  const { timer: _timer, slowTimer: _slowTimer, pairingInFlight: _pairingInFlight, ...operation } = pending;
  return operation;
}

function basename(path: string | null): string | null {
  if (!path) return null;
  const parts = path.replace(/[\\/]+$/, '').split(/[\\/]/);
  return parts[parts.length - 1] || path;
}

function titleFor(channelId: string, cwd: string | null, stored: string | null): string {
  return stored || basename(cwd) || creativeName(channelId);
}

function isUnreadActivity(message: EventEnvelope): boolean {
  switch (message.eventType) {
    case EVENT_TYPE.STREAM:
      return message.eventSubtype !== SUBTYPE.STREAM.USER_MESSAGE;
    case EVENT_TYPE.APPROVAL:
      return message.eventSubtype === SUBTYPE.APPROVAL.REQUEST;
    case EVENT_TYPE.ELICITATION:
      return message.eventSubtype === SUBTYPE.ELICITATION.REQUEST;
    case EVENT_TYPE.CONTROL:
      return message.eventSubtype === SUBTYPE.CONTROL.CHANNEL_DOWN;
    default:
      return false;
  }
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** The real CLI title an inbound message carries (channel_up / session_meta), or undefined otherwise.
 *  Only a genuine title is persisted; the cwd-basename fallback is display-only. */
function reportedTitle(message: EventEnvelope): string | undefined {
  if (message.eventType !== EVENT_TYPE.CONTROL) return undefined;
  if (
    message.eventSubtype === SUBTYPE.CONTROL.CHANNEL_UP ||
    message.eventSubtype === SUBTYPE.CONTROL.SESSION_META
  ) {
    const title = (message.msg as { title?: string }).title;
    return title || undefined;
  }
  return undefined;
}

/**
 * The I/O EDGE and the `manager` facade in one object. It owns the sockets (through the transport
 * registry) and the per-channel controllers (timers + subscriptions), runs the honest-liveness FSM,
 * the warm-pool LRU, the watchdog and the resume triggers, and reflects every observable change into
 * the RTK `sessions` store. Its public surface is byte-for-byte the pre-refactor `SessionManager`, so
 * the UI (and the scenario harness) can drive it unchanged: `subscribe` = store subscription,
 * `getSnapshot` = the `selectManagerSnapshot` reprojection.
 */
export class SessionRuntime {
  private readonly store: AppStore;
  private readonly registry: TransportRegistry;
  private readonly clock: () => number;
  private readonly controllers = new Map<string, ChannelController>();
  private readonly listenerControllers = new Map<string, ChannelController>();
  private readonly pendingSpawns = new Map<string, PendingSpawn>();
  private readonly lifecycleStatuses = new Map<string, LifecycleStatusMsg>();
  private readonly lifecycleWaiters = new Map<string, Set<LifecycleWaiter>>();
  private warmLru: string[] = [];
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private initStarted = false;
  private eventSeq = 0;
  private resumeCleanups: Array<() => void> = [];
  private lastResumeAt = 0;
  /** Per-device throttle stamps (ms) for the watchdog's self-heal reconnect, so a wedged offline
   *  device is re-established on a backoff (deviceReconnectBackoffMs) without hammering the relay. */
  private readonly deviceReconnectAt = new Map<string, number>();
  /** Per-device count of consecutive self-heal reconnect attempts since the device was last healthy.
   *  Feeds the exponential half of deviceReconnectBackoffMs; reset to 0 on health, on app foreground,
   *  and on boot so a returning user always gets an immediate (attempt-0) reconnect. */
  private readonly deviceReconnectFails = new Map<string, number>();
  /** A replacement laptop runtime can join the same relay room without disconnecting the phone's
   * socket. Heartbeat silence must therefore drive a fresh pairing hello, with bounded retries. */
  private readonly sessionReconnectAt = new Map<string, number>();
  private readonly sessionReconnectFails = new Map<string, number>();
  /** Per-device fail-safe timers for an outstanding `project_list_request` (see
   *  PROJECT_LIST_INFLIGHT_MS) — while an entry exists, refreshProjects skips sending a duplicate. */
  private readonly projectListInflight = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly deviceMonitors = new Map<string, DeviceMonitorLease>();
  private readonly terminals = new Map<string, TerminalController>();
  private readonly demoDevices = new Map<string, string>();
  /** Persist the first fresh sample of a visit immediately, then at most once a minute while the
   *  page stays open. This keeps the cache useful without writing native Preferences every tick. */
  private readonly deviceHealthCacheWrittenAt = new Map<string, number>();
  /** Per-session throttle stamps (ms) for the witnessed-liveness persist (#163). Runtime-level (not
   *  on the ChannelController) so the witness can keep advancing for cold/archived sessions too. */
  private readonly lastLivenessWriteAt = new Map<string, number>();
  /** Last observed pulse per session, surviving a reload. `connection.lastHeartbeat` lives only in
   *  memory and comes back null for every restored card, so anything that reads it alone treats a
   *  session that has never reconnected as "no pulse ever" and skips it forever. Seeded from stored
   *  `lastHeartbeatAt` at boot and kept in step with the live beat. */
  private readonly lastKnownHeartbeat = new Map<string, number>();
  // requestIds of approvals/elicitations whose decision send is in flight. Guards a double-tap from
  // dispatching twice (#88) and lets onMessage strip an already-answered card from a racing state
  // snapshot before the send settles (#79).
  private readonly inFlightDecisions = new Set<string>();

  constructor(store?: AppStore) {
    this.store = store ?? makeStore();
    const deps: RuntimeDeps = this.store.deps;
    this.registry = deps.registry;
    this.clock = deps.clock;
  }

  // --- facade (useSyncExternalStore) ---------------------------------------------------------------
  subscribe = (listener: () => void): (() => void) => this.store.subscribe(listener);
  getSnapshot = (): ManagerSnapshot => selectManagerSnapshot(this.store.getState());
  getLifecycleStatus = (operationId: string): LifecycleStatusMsg | null =>
    this.lifecycleStatuses.get(operationId) ?? null;

  async confirmLifecycleTakeover(
    channelId: string,
    operationId: string,
    challengeId: string,
    revision: number,
  ): Promise<LifecycleStatusMsg> {
    return this.sendLifecycleCommand(
      channelId,
      operationId,
      revision,
      takeoverConfirmMessage(operationId, challengeId, revision),
    );
  }

  async cancelLifecycleOperation(
    channelId: string,
    operationId: string,
    revision: number,
  ): Promise<LifecycleStatusMsg> {
    return this.sendLifecycleCommand(
      channelId,
      operationId,
      revision,
      lifecycleCancelMessage(operationId, revision),
    );
  }

  private activeId(): string | null {
    return this.store.getState().sessions.activeId;
  }

  private session(id: string): Session | undefined {
    return this.store.getState().sessions.entities[id];
  }

  private ensureController(id: string, opts: { ephemeral?: boolean } = {}): ChannelController {
    let ctrl = this.controllers.get(id);
    if (!ctrl) {
      ctrl = new ChannelController(id, opts);
      this.controllers.set(id, ctrl);
    }
    return ctrl;
  }

  private device(channelId: string): ListenerDeviceState | undefined {
    return this.store.getState().sessions.devices.find((d) => d.channelId === channelId);
  }

  private listenerController(channelId: string): ChannelController | undefined {
    return this.listenerControllers.get(channelId);
  }

  private ensureListenerController(channelId: string): ChannelController {
    let ctrl = this.listenerControllers.get(channelId);
    if (!ctrl) {
      ctrl = new ChannelController(channelId, { ephemeral: true });
      this.listenerControllers.set(channelId, ctrl);
    }
    return ctrl;
  }

  // --- lifecycle -----------------------------------------------------------------------------------
  async init(): Promise<void> {
    if (this.initStarted) return;
    this.initStarted = true;
    void ensureNotificationPermission();
    this.startWatchdog();
    this.installResumeTriggers();

    let stored: StoredSession[] = [];
    let devices: RegisteredDevice[] = [];
    let pendingOperations: PendingOperation[] = [];
    let lastActiveId: string | null = null;
    try {
      [stored, lastActiveId, devices, pendingOperations] = await Promise.all([
        loadSessions(),
        loadLastActiveSessionId(),
        loadDevices(),
        loadPendingOperations(),
      ]);
    } catch {
      stored = [];
      devices = [];
      pendingOperations = [];
      lastActiveId = null;
    }

    // #163 boot delete-sweep: purge sessions whose *witnessed* silence already exceeds the delete
    // window, straight from storage, BEFORE they are restored into the store (remove() can't run yet
    // — there's no card in state). The last-active session is always spared so activation never
    // dangles. Pinned sessions are exempt (isDeleteEligible checks that).
    const doomed = stored.filter((s) => this.isDeleteEligible(s, lastActiveId));
    if (doomed.length > 0) {
      const doomedIds = new Set(doomed.map((s) => s.pairing.channelId));
      await Promise.all(
        doomed.map(async (s) => {
          const channelId = s.pairing.channelId;
          await removeSession(channelId).catch(() => {});
          await clearTranscript(channelId).catch(() => {});
          await clearEventLog(channelId).catch(() => {});
        }),
      );
      stored = stored.filter((s) => !doomedIds.has(s.pairing.channelId));
    }

    this.store.dispatch(
      devicesHydrated(
        await Promise.all(
          devices.map(async (device) => ({
            ...device,
            projects: [],
            projectsLoading: false,
            connected: false,
            events: await loadEventLog(device.channelId).catch(() => []),
          })),
        ),
      ),
    );

    const restored = await Promise.all(
      stored.map(async (s) => ({
        channelId: s.pairing.channelId,
        persisted: await loadTranscript(s.pairing.channelId).catch(() => null),
        events: await loadEventLog(s.pairing.channelId).catch(() => []),
      })),
    );
    const persistedById = new Map(restored.map((r) => [r.channelId, r.persisted]));
    const eventsById = new Map(restored.map((r) => [r.channelId, r.events]));

    const recencyOf = (s: StoredSession): number =>
      Math.max(s.lastEventAt ?? 0, s.lastSeenAt ?? 0, s.addedAt ?? 0);
    const warmIds = new Set(
      [...stored]
        .sort((a, b) => recencyOf(b) - recencyOf(a))
        .slice(0, MAX_WARM_SESSIONS)
        .map((s) => s.pairing.channelId),
    );

    let activeId: string | null = null;
    if (stored.length > 0) {
      const lastActiveExists = lastActiveId
        ? stored.some((s) => s.pairing.channelId === lastActiveId)
        : false;
      const first = lastActiveExists ? null : [...stored].sort((a, b) => recencyOf(b) - recencyOf(a))[0];
      activeId = lastActiveExists ? lastActiveId : (first?.pairing.channelId ?? stored[0].pairing.channelId);
      if (activeId) warmIds.add(activeId);
    }

    // #163 boot auto-archive: sessions whose *witnessed* silence already crossed the 6h archive
    // window (but not the 2-day delete window handled above) start in the calm Archived state —
    // cold, no live socket, out of the warm pool — rather than burning a reconnect on a laptop
    // we last heard from hours ago. The user taps to reconnect. Active/pinned are spared.
    const archivedIds = new Set(
      stored.filter((s) => this.isArchiveEligible(s, activeId)).map((s) => s.pairing.channelId),
    );
    for (const id of archivedIds) warmIds.delete(id);

    for (const s of stored) {
      const channelId = s.pairing.channelId;
      allowTranscriptWrites(channelId);
      if (s.lastHeartbeatAt != null) this.lastKnownHeartbeat.set(channelId, s.lastHeartbeatAt);
      const timeline = restoreTimeline(persistedById.get(channelId) ?? null);
      const session = this.buildRestoredSession(
        s,
        timeline,
        eventsById.get(channelId) ?? [],
        warmIds.has(channelId) ? 'connecting' : 'idle',
        archivedIds.has(channelId),
      );
      this.store.dispatch(sessionAdded(session));
      this.controllers.set(channelId, new ChannelController(channelId));
    }
    for (const operation of pendingOperations) {
      const session = emptySession(operation.tempId, {
        channelId: operation.tempId,
        sessionId: operation.sessionId,
        title: operation.title || operation.name || operation.projectName,
        cwd: operation.cwd ?? null,
        kind: 'spawning',
        addedAt: operation.createdAt,
        spawnedFromDeviceId: operation.spawnedFromDeviceId,
        spawnedFromDeviceName: operation.spawnedFromDeviceName,
      });
      session.connection.status = operation.stage === 'pairing-failed' ? 'error' : 'initializing';
      session.connection.error =
        operation.stage === 'pairing-failed'
          ? 'The session started, but the phone could not connect yet. Try again to reconnect to it.'
          : undefined;
      session.connection.spawning = {
        requestId: operation.requestId,
        deviceId: operation.deviceId,
        deviceName: operation.spawnedFromDeviceName,
        projectName: operation.projectName,
        operation: operation.kind,
        stage: operation.stage,
        createdAt: operation.createdAt,
        mode: operation.mode,
        slow: this.clock() - operation.createdAt >= RESUME_SLOW_MS,
      };
      this.pendingSpawns.set(operation.requestId, { ...operation });
      if (operation.lifecycleStatus) {
        this.lifecycleStatuses.set(operation.requestId, operation.lifecycleStatus);
      }
      this.store.dispatch(sessionAdded(session));
    }
    if (!activeId && pendingOperations.length > 0) {
      activeId = [...pendingOperations].sort((a, b) => b.createdAt - a.createdAt)[0]?.tempId ?? null;
    }
    if (activeId) this.store.dispatch(sessionActivated(activeId));
    this.store.dispatch(readySet(true));

    // Auto-clean storage now that the live set is known: drop orphaned transcripts/event-logs (from
    // crashed/reconciled channels no longer in the list) and, if still over budget, trim the oldest
    // cold entries. Never touches the session list, pairing, or the active/warm channels — so no
    // session data or connection is lost. Best-effort; a failure here must not block boot.
    void sweepStorage({
      validChannelIds: [...stored.map((s) => s.pairing.channelId), ...devices.map((d) => d.channelId)],
      protectChannelIds: warmIds,
    }).catch(() => {});

    await Promise.all(
      stored
        .filter((s) => warmIds.has(s.pairing.channelId))
        .map(async (s) => {
          const channelId = s.pairing.channelId;
          const ctrl = this.ensureController(channelId);
          if (ctrl.reconnecting) return;
          ctrl.reconnecting = true;
          try {
            const client = await connectSession(s.pairing);
            this.attach(channelId, client);
          } catch (err) {
            this.store.dispatch(
              statusSet({ id: channelId, status: 'error', error: errMessage(err, 'Failed to reconnect.') }),
            );
          } finally {
            if (this.controllers.get(channelId) === ctrl) ctrl.reconnecting = false;
          }
        }),
    );

    // Every registered device reconnects on boot, unlike sessions' warm/cold split — the hard
    // MAX_DEVICES=10 cap (see registerListenerFromQr) already bounds how many live listener
    // sockets this can ever open, so there's no need for a warm-pool LRU here.
    await Promise.all(devices.map((d) => this.connectDevice(d.channelId)));

    // Pairing material proves the Device Station accepted and launched this operation. Recover it
    // automatically after an app restart without sending another spawn request. Operations that
    // were never acknowledged stay manual so an offline laptop cannot open a surprise terminal
    // hours later merely because it reconnected.
    for (const pending of this.pendingSpawns.values()) {
      if (pending.pairingPayload) void this.attemptPendingPair(pending.requestId);
    }
  }

  /** Rebuild a store `Session` from a restored transcript + stored metadata (mirrors restoreTimeline:
   *  live/transient fields — busy, heartbeat, approvals — start fresh; the persisted subset carries). */
  private buildRestoredSession(
    stored: StoredSession,
    timeline: TimelineState,
    events: Session['debug'],
    status: Session['connection']['status'],
    cold = false,
  ): Session {
    const channelId = stored.pairing.channelId;
    const cwd = timeline.cwd ?? stored.cwd;
    const meta: SessionMeta = {
      channelId,
      sessionId: stored.sessionId ?? undefined,
      // A renamed session pins the user's stored title; otherwise prefer the freshest CLI title from
      // the restored transcript, falling back to the stored title / cwd / creative name.
      title: stored.renamed
        ? stored.title || titleFor(channelId, cwd, null)
        : titleFor(channelId, cwd, timeline.title ?? stored.title),
      reportedTitle: stored.reportedTitle
        ?? (!stored.renamed ? (timeline.title ?? stored.title ?? undefined) : undefined),
      renamed: stored.renamed ?? false,
      cwd,
      kind: 'live',
      addedAt: stored.addedAt,
      scannedAt: stored.pairing.savedAt ?? stored.addedAt,
      spawnedFromDeviceId: stored.spawnedFromDeviceId,
      spawnedFromDeviceName: stored.spawnedFromDeviceName,
      transport: stored.pairing.transport?.kind,
      laptopVersion: stored.pairing.appVersion,
    };
    const session = emptySession(channelId, meta);
    session.unread = stored.unread ?? false;
    session.unreadCount = stored.unreadCount ?? (stored.unread ? 1 : 0);
    session.lastEventAt = stored.lastEventAt ?? null;
    session.pinned = stored.pinned ?? false;
    session.transcript.items = timeline.items;
    session.history = {
      items: timeline.history,
      cursor: timeline.historyCursor,
      hasMore: timeline.historyHasMore,
      loading: false,
      latestTurnIndex: timeline.latestTurnIndex,
    };
    session.connection.mode = timeline.mode;
    session.connection.status = status;
    session.connection.cold = cold;
    session.debug = events;
    return session;
  }

  // --- connecting / attaching ----------------------------------------------------------------------
  private attach(channelId: string, client: WeftClient): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || !this.session(channelId)) {
      void client.close().catch(() => {});
      return;
    }
    const previous = ctrl.client;
    ctrl.detach();
    ctrl.client = client;
    this.registry.adopt(channelId, client);
    if (previous && previous !== client) void previous.close().catch(() => {});

    const stopEvents = client.subscribe((message) => this.onMessage(channelId, client, message));
    const stopStatus = client.onStatus((status) => this.handleTransportStatus(channelId, client, status));
    ctrl.unsubscribe = () => {
      stopEvents();
      stopStatus();
    };

    if (ctrl.ephemeral) {
      this.store.dispatch(statusSet({ id: channelId, status: 'live', error: undefined }));
    } else {
      this.touchWarm(channelId);
      this.beginHostConfirm(channelId, client);
      // We now hold an open subscription: stamp the witness clock (#163) so witnessed-silence and
      // boot-probe ordering have a start edge even before the first heartbeat lands.
      this.persistLiveness(channelId, /* flush */ true);
    }
    this.requestState(channelId);
    this.syncHistory(channelId, client);
  }

  /** Enter unconfirmed 'connecting' and arm the liveness deadline. The single honest-Live guard. */
  private beginHostConfirm(channelId: string, client: WeftClient): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral || ctrl.client !== client) return;
    const session = this.session(channelId);
    if (!session || session.connection.status === 'ended') return;
    this.store.dispatch(statusSet({ id: channelId, status: 'connecting', error: undefined }));
    ctrl.connectingSince = this.clock();
    this.clearSettle(channelId);
    ctrl.arm(
      'confirm',
      () => {
        const c = this.controllers.get(channelId);
        if (!c || c.client !== client) return;
        c.clear('confirm');
        const s = this.session(channelId);
        if (!s || s.connection.status !== 'connecting') return;
        this.store.dispatch(
          statusSet({
            id: channelId,
            status: 'error',
            error: 'Copilot terminal offline — reopen it on your laptop and scan the new QR.',
          }),
        );
        if (s.connection.busy) this.store.dispatch(busySet({ id: channelId, busy: false }));
      },
      HOST_CONFIRM_MS,
    );
  }

  /** Settle the confirmation gate on the FIRST genuine inbound signal (status→live is done by the
   *  reducer; here we clear the deadline and manage the bounded initial-skeleton grace). */
  private confirmHost(channelId: string, wasLive: boolean): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl) return;
    ctrl.clear('confirm');
    ctrl.connectingSince = null;
    this.sessionReconnectAt.delete(channelId);
    this.sessionReconnectFails.delete(channelId);
    if (wasLive) return;
    const s = this.session(channelId);
    if (!s) return;
    const emptyAndLoading =
      s.transcript.items.length === 0 && s.history.items.length === 0 && s.history.loading;
    if (emptyAndLoading) this.beginSettle(channelId);
    else this.clearSettle(channelId);
  }

  private beginSettle(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl) return;
    const s = this.session(channelId);
    if (s && !s.connection.settling) this.store.dispatch(settlingSet({ id: channelId, on: true }));
    ctrl.arm(
      'settle',
      () => {
        ctrl.clear('settle');
        const cur = this.session(channelId);
        if (cur && cur.connection.settling) this.store.dispatch(settlingSet({ id: channelId, on: false }));
      },
      INITIAL_HISTORY_GRACE_MS,
    );
  }

  private clearSettle(channelId: string): void {
    this.controllers.get(channelId)?.clear('settle');
    const s = this.session(channelId);
    if (s && s.connection.settling) this.store.dispatch(settlingSet({ id: channelId, on: false }));
  }

  private handleTransportStatus(
    channelId: string,
    client: WeftClient,
    status: 'connected' | 'disconnected',
  ): void {
    const ctrl = this.controllers.get(channelId);
    const session = this.session(channelId);
    if (!ctrl || !session || ctrl.client !== client || ctrl.ephemeral || session.connection.status === 'ended') {
      return;
    }
    if (status === 'disconnected') {
      if (session.connection.status === 'live' || session.connection.status === 'idle') {
        this.store.dispatch(
          statusSet({ id: channelId, status: 'error', error: 'Connection lost — reconnect to resume.' }),
        );
        if (session.connection.busy) this.store.dispatch(busySet({ id: channelId, busy: false }));
      }
      return;
    }
    if (session.connection.status === 'error') {
      this.beginHostConfirm(channelId, client);
      this.requestState(channelId);
      this.syncHistory(channelId, client);
    }
  }

  private requestState(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral || !ctrl.client) return;
    void this.send(channelId, stateRequest()).catch(() => {
      // A failed state request must never break the connection; live events still flow.
    });
  }

  private syncHistory(channelId: string, _client: WeftClient): void {
    const ctrl = this.controllers.get(channelId);
    const session = this.session(channelId);
    if (!ctrl || !session || ctrl.ephemeral) return;
    const hasContent = session.history.items.length > 0 || session.transcript.items.length > 0;
    if (!hasContent) {
      this.store.dispatch(historyLoadingSet({ id: channelId, loading: true }));
      this.armHistoryTimeout(channelId);
    }
    void this.send(channelId, recentTurnsRequest(RECENT_TURNS_DEFAULT)).catch(() => {
      this.store.dispatch(historyLoadingSet({ id: channelId, loading: false }));
      this.clearHistoryTimeout(channelId);
    });
  }

  private armHistoryTimeout(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl) return;
    ctrl.arm(
      'history',
      () => {
        ctrl.clear('history');
        const s = this.session(channelId);
        if (s && s.history.loading) this.store.dispatch(historyLoadingSet({ id: channelId, loading: false }));
      },
      HISTORY_REQUEST_TIMEOUT_MS,
    );
  }

  private clearHistoryTimeout(channelId: string): void {
    this.controllers.get(channelId)?.clear('history');
  }

  // --- inbound -------------------------------------------------------------------------------------
  /**
   * Strip approvals/elicitations the user has already answered (decision send still in flight) from
   * an authoritative STATE_SNAPSHOT, so a snapshot the extension emitted before it observed our
   * decision can't resurrect a dismissed card (#79). Non-snapshot envelopes pass through untouched.
   */
  private filterInFlight(message: EventEnvelope): EventEnvelope {
    if (
      this.inFlightDecisions.size === 0 ||
      message.eventType !== EVENT_TYPE.CONTROL ||
      message.eventSubtype !== SUBTYPE.CONTROL.STATE_SNAPSHOT
    ) {
      return message;
    }
    const snap = message.msg as StateSnapshotMsg;
    return {
      ...message,
      msg: {
        ...snap,
        approvals: (snap.approvals ?? []).filter((a) => !this.inFlightDecisions.has(a.requestId)),
        elicitations: (snap.elicitations ?? []).filter(
          (e) => !this.inFlightDecisions.has(e.requestId),
        ),
      },
    };
  }

  private onMessage(channelId: string, client: WeftClient, message: EventEnvelope): void {
    if (isTerminalEnvelope(message)) return;
    const ctrl = this.controllers.get(channelId);
    const before = this.session(channelId);
    if (!ctrl || !before) return;
    // Ignore a message that decrypted after its channel was superseded (reconnect) or cooled to cold:
    // acting on it could set a stale card back to 'live' with no live connection.
    if (ctrl.client !== client) return;

    // Stamp a phone-local receipt time the instant we receive the envelope. All downstream
    // elapsed-time math (heartbeat liveness in applyEnvelope, witnessed-silence) uses THIS, never
    // `message.ts` — that field is the laptop's clock and comparing it to the phone's clock flaps
    // sessions idle/offline (and skews "N ago") whenever the two machines' clocks disagree.
    message.receivedAt = this.clock();

    const wasLive = before.connection.status === 'live';
    const prevCwd = before.meta.cwd;
    const prevSessionId = before.meta.sessionId;
    const prevTitle = before.meta.title;
    const prevReportedTitle = before.meta.reportedTitle;
    const prevRenamed = before.meta.renamed;

    this.recordEvent(channelId, 'in', message);
    this.store.dispatch(envelopeReceived({ id: channelId, envelope: this.filterInFlight(message) }));

    if (isUnreadActivity(message)) this.markActivity(channelId);

    const isControl = message.eventType === EVENT_TYPE.CONTROL;
    const sub = message.eventSubtype;

    if (isControl && sub === SUBTYPE.CONTROL.HISTORY) {
      this.clearHistoryTimeout(channelId);
      this.clearSettle(channelId);
    }
    if (isControl && sub === SUBTYPE.CONTROL.RECENT_TURNS) {
      this.clearHistoryTimeout(channelId);
      this.clearSettle(channelId);
    }

    if (isControl && sub === SUBTYPE.CONTROL.CHANNEL_DOWN) {
      ctrl.clear('confirm');
      ctrl.connectingSince = null;
      this.clearSettle(channelId);
      void notifySessionEnded(this.session(channelId)?.connection.endedReason);
    } else {
      this.confirmHost(channelId, wasLive);
    }

    if (isControl && sub === SUBTYPE.CONTROL.CHANNEL_UP) {
      const sid = message.sessionId;
      if (sid && sid !== 'unknown-session') this.reconcileBySessionId(channelId, sid);
      // The first recent-turns request goes out the moment the socket attaches, which can be
      // hundreds of milliseconds before the laptop announces the channel is actually up. Anything
      // sent in that window is dropped on the floor, and nothing retries it — the thread just sits
      // on the empty state until a full reconnect. Asking again once the channel is provably up
      // costs nothing, and only when we still have nothing to show, so a thread that did receive
      // its history is never asked twice.
      const cur = this.session(channelId);
      const empty = !cur || (cur.history.items.length === 0 && cur.transcript.items.length === 0);
      if (empty && ctrl.client && !ctrl.ephemeral) this.syncHistory(channelId, ctrl.client);
    }

    // Persist any changed durable metadata as ONE coalesced patch. Separate un-awaited patchSession
    // calls each do a read-modify-write of the whole session list, so concurrent ones clobber each
    // other; a single patch (only the keys that actually changed) is race-free. Title persists only
    // the genuine CLI title, never the cwd-basename fallback.
    if (!ctrl.ephemeral) {
      const cur = this.session(channelId);
      if (cur) {
        const patch: Partial<StoredSession> = {};
        if (isControl && sub === SUBTYPE.CONTROL.CHANNEL_UP) {
          const sid = message.sessionId;
          if (sid && sid !== 'unknown-session' && sid !== prevSessionId) patch.sessionId = sid;
        }
        if (cur.meta.cwd && cur.meta.cwd !== prevCwd) patch.cwd = cur.meta.cwd;
        const title = reportedTitle(message);
        if (title && cur.meta.reportedTitle !== prevReportedTitle)
          patch.reportedTitle = cur.meta.reportedTitle ?? title;
        if (title && cur.meta.title !== prevTitle) patch.title = cur.meta.title;
        if (cur.meta.renamed !== prevRenamed) patch.renamed = cur.meta.renamed;
        if (Object.keys(patch).length > 0) void patchSession(channelId, patch);
      }
    }

    if (message.eventType === EVENT_TYPE.APPROVAL && sub === SUBTYPE.APPROVAL.REQUEST) {
      void notifyApprovalRequest(message.msg);
    }
    if (message.eventType === EVENT_TYPE.ELICITATION && sub === SUBTYPE.ELICITATION.REQUEST) {
      void notifyElicitationRequest(message.msg);
    }

    this.schedulePersist(channelId);
  }

  /** Collapse a stale duplicate card that reports a sessionId we already track under another channel
   *  (a `copilot --resume` rotated the channelId). State merge is the reducer's job; here we tear down
   *  the merged duplicate's socket, timers, and stored state. */
  private reconcileBySessionId(keepId: string, sessionId: string): void {
    const duplicate = selectAllSessions(this.store.getState()).find(
      (s) => s.id !== keepId && s.meta.sessionId === sessionId && !this.controllers.get(s.id)?.ephemeral,
    );
    this.store.dispatch(sessionReconciled({ id: keepId, sessionId }));
    if (!duplicate) return;
    this.controllers.get(duplicate.id)?.dispose();
    this.controllers.delete(duplicate.id);
    this.registry.dispose(duplicate.id);
    this.warmLru = this.warmLru.filter((id) => id !== duplicate.id);
    discardTranscriptWrites(duplicate.id);
    void removeSession(duplicate.id);
    void clearTranscript(duplicate.id);
    void clearEventLog(duplicate.id);
  }

  // --- warm pool -----------------------------------------------------------------------------------
  private markActivity(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral) return;
    this.store.dispatch(lastEventAtSet({ id: channelId, ts: this.clock() }));
    this.touchWarm(channelId);
    this.scheduleMetaPersist(channelId);
  }

  private touchWarm(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral) return;
    const session = this.session(channelId);
    if (!session || session.connection.status === 'ended') return;
    const i = this.warmLru.indexOf(channelId);
    if (i >= 0) this.warmLru.splice(i, 1);
    this.warmLru.push(channelId);
    this.evictBeyondWarmLimit();
  }

  private evictBeyondWarmLimit(): void {
    const activeId = this.activeId();
    while (this.warmLru.length > MAX_WARM_SESSIONS) {
      const victimIndex = this.warmLru.findIndex((id) => id !== activeId);
      if (victimIndex < 0) break;
      const [victimId] = this.warmLru.splice(victimIndex, 1);
      this.coolDown(victimId);
    }
  }

  /** Drop a session's live subscription/socket while keeping its card, transcript, and unread. */
  private coolDown(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    const session = this.session(channelId);
    if (!ctrl || !session || ctrl.ephemeral) return;
    // Leave the warm pool so the foreground resume sweep + eviction stop treating it as a live
    // member (an archived session must not be auto-revived on every foreground — the user taps to
    // reconnect). Idempotent: eviction already splices the victim before calling here.
    this.warmLru = this.warmLru.filter((id) => id !== channelId);
    ctrl.dispose();
    this.controllers.delete(channelId);
    this.registry.dispose(channelId);
    if (session.connection.status !== 'ended') {
      this.store.dispatch(statusSet({ id: channelId, status: 'idle', error: undefined }));
      this.store.dispatch(coldSet({ id: channelId, on: true }));
    }
    if (session.connection.busy) this.store.dispatch(busySet({ id: channelId, busy: false }));
    this.persistLiveness(channelId, true);
  }

  private ensureConnected(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    const session = this.session(channelId);
    if (!session || session.connection.status === 'ended') return;
    if (ctrl?.ephemeral) return;
    if (session.connection.status === 'live' || session.connection.status === 'connecting') return;
    void this.reconnect(channelId);
  }

  // --- adding sessions -----------------------------------------------------------------------------
  private async openPairedSession(
    client: WeftClient,
    pairing: StoredPairing,
    opts: {
      title?: string | null;
      cwd?: string | null;
      activate?: boolean;
      scannedAt?: number;
      renamed?: boolean;
      spawnedFromDeviceId?: string;
      spawnedFromDeviceName?: string;
    } = {},
  ): Promise<string> {
    const channelId = pairing.channelId;
    allowTranscriptWrites(channelId);
    const existing = this.session(channelId);
    const existingCtrl = this.controllers.get(channelId);
    if (existing && !existingCtrl?.ephemeral) {
      // Rescanning a channel we already track: refresh the pairing + rebind a fresh client, KEEP the
      // transcript. attach() then forward-catches-up rather than pulling from scratch.
      const prior = (await loadSessions()).find((s) => s.pairing.channelId === channelId);
      await upsertSession({
        pairing,
        sessionId: prior?.sessionId ?? existing.meta.sessionId ?? null,
        title: prior?.title ?? null,
        reportedTitle: prior?.reportedTitle ?? existing.meta.reportedTitle ?? null,
        cwd: prior?.cwd ?? null,
        addedAt: prior?.addedAt ?? existing.meta.addedAt,
        lastSeenAt: this.clock(),
        spawnedFromDeviceId: prior?.spawnedFromDeviceId ?? existing.meta.spawnedFromDeviceId,
        spawnedFromDeviceName: prior?.spawnedFromDeviceName ?? existing.meta.spawnedFromDeviceName,
      });
      const ctrl = this.ensureController(channelId);
      try {
        await ctrl.client?.close();
      } catch {
        // The old socket may already be gone; the fresh client supersedes it either way.
      }
      ctrl.client = null;
      this.registry.dispose(channelId);
      this.store.dispatch(metaScannedAtSet({ id: channelId, ts: pairing.savedAt ?? this.clock(), transport: pairing.transport?.kind }));
      if (opts.activate !== false) this.store.dispatch(sessionActivated(channelId));
      this.attach(channelId, client);
      return channelId;
    }

    const now = this.clock();
    await upsertSession({
      pairing,
      title: opts.title ?? null,
      reportedTitle: opts.title ?? null,
      cwd: opts.cwd ?? null,
      addedAt: now,
      lastSeenAt: now,
      renamed: opts.renamed,
      spawnedFromDeviceId: opts.spawnedFromDeviceId,
      spawnedFromDeviceName: opts.spawnedFromDeviceName,
    });
    const meta: SessionMeta = {
      channelId,
      title: titleFor(channelId, opts.cwd ?? null, opts.title ?? null),
      reportedTitle: opts.title ?? undefined,
      renamed: opts.renamed ?? false,
      cwd: opts.cwd ?? null,
      kind: 'live',
      addedAt: now,
      scannedAt: opts.scannedAt ?? pairing.savedAt ?? now,
      spawnedFromDeviceId: opts.spawnedFromDeviceId,
      spawnedFromDeviceName: opts.spawnedFromDeviceName,
      transport: pairing.transport?.kind,
      laptopVersion: pairing.appVersion,
    };
    const session = emptySession(channelId, meta);
    session.connection.status = 'connecting';
    this.store.dispatch(sessionAdded(session));
    this.controllers.set(channelId, new ChannelController(channelId));
    if (opts.activate !== false) this.store.dispatch(sessionActivated(channelId));
    this.attach(channelId, client);
    return channelId;
  }

  async addByQr(raw: string): Promise<string> {
    let parsed: ReturnType<typeof parsePairingPayload> | null = null;
    try {
      parsed = parsePairingPayload(raw);
    } catch {
      // The test harness historically uses plain channel ids as QR strings; pairSession remains the
      // single authoritative parser on the real path and will surface invalid production payloads.
    }
    if (parsed?.kind === PAIR_KIND.LISTENER) return this.registerListenerFromQr(raw);
    const prior = parsed
      ? (await loadSessions()).find(
          (entry) =>
            entry.pairing.channelId === parsed.channelId &&
            entry.pairing.peerPublicKeyB64 === parsed.publicKeyB64,
        )
      : undefined;
    const identity = prior
      ? {
          publicKeyB64: prior.pairing.publicKeyB64,
          privateKeyJwk: prior.pairing.privateKeyJwk,
          deviceId: prior.pairing.deviceId,
        }
      : undefined;
    const { client, pairing } = await pairSession(raw, { identity });
    return this.openPairedSession(client, pairing);
  }

  async addDemo(): Promise<string> {
    let demo: DemoSession;
    try {
      demo = await startDemoSession();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to start demo.');
    }
    const channelId = demo.channelId;
    allowTranscriptWrites(channelId);
    const meta: SessionMeta = {
      channelId,
      title: 'Demo session',
      cwd: '/home/user/my-project',
      kind: 'demo',
      addedAt: this.clock(),
    };
    const session = emptySession(channelId, meta);
    session.connection.status = 'connecting';
    this.store.dispatch(sessionAdded(session));
    const ctrl = new ChannelController(channelId, { ephemeral: true });
    ctrl.stopDemo = demo.stop;
    ctrl.client = demo.client;
    this.controllers.set(channelId, ctrl);
    this.registry.adopt(channelId, demo.client);
    this.store.dispatch(sessionActivated(channelId));
    this.attach(channelId, demo.client);
    if (demo.station) {
      const { pairing, client: stationClient } = demo.station;
      const deviceChannelId = pairing.channelId;
      this.demoDevices.set(channelId, deviceChannelId);
      this.store.dispatch(deviceUpserted({
        channelId: deviceChannelId, pub: pairing.peerPublicKeyB64,
        transport: pairing.transport, publicKeyB64: pairing.publicKeyB64, privateKeyJwk: pairing.privateKeyJwk,
        name: 'Demo laptop', savedAt: this.clock(), isDefault: false,
        projects: [], projectsLoading: false, connected: true, events: [],
      }));
      this.attachListener(deviceChannelId, stationClient);
    }
    return channelId;
  }

  // --- listener devices / phone-launched sessions -------------------------------------------------
  async registerListenerFromQr(raw: string): Promise<string> {
    const parsed = parsePairingPayload(raw);
    if (parsed.kind !== PAIR_KIND.LISTENER) throw new Error('This QR is not a listener device.');
    const channelId = parsed.channelId;
    const prior = this.device(channelId);
    // Only a genuinely NEW device counts against the cap — re-scanning an already-registered
    // listener (e.g. after `weft start` was restarted and minted a fresh channelId, before
    // reconcileDevice folds it in) must never be blocked by its own prior entry.
    if (!prior && this.store.getState().sessions.devices.length >= MAX_DEVICES) {
      throw new Error(
        `You already have ${MAX_DEVICES} devices connected — forget one (Devices → Forget) before adding another.`,
      );
    }
    const identity = prior && prior.pub === parsed.publicKeyB64
      ? {
          publicKeyB64: prior.publicKeyB64,
          privateKeyJwk: prior.privateKeyJwk,
          deviceId: getStableDeviceId(),
        }
      : undefined;
    const { client, pairing } = await pairSession(raw, { identity });
    const now = this.clock();
    const stored: RegisteredDevice = {
      channelId: pairing.channelId,
      pairVersion: pairing.pairVersion,
      pub: pairing.peerPublicKeyB64,
      transport: pairing.transport,
      publicKeyB64: pairing.publicKeyB64,
      privateKeyJwk: pairing.privateKeyJwk,
      name: prior?.name,
      savedAt: now,
      isDefault: prior?.isDefault ?? this.store.getState().sessions.devices.length === 0,
      lastProjectName: prior?.lastProjectName,
      appVersion: pairing.appVersion ?? prior?.appVersion,
    };
    await upsertDevice(stored);
    this.store.dispatch(
      deviceUpserted({
        ...stored,
        projects: prior?.projects ?? [],
        capabilities: prior?.capabilities,
        projectsLoading: true,
        connected: true,
        events: prior?.events ?? (await loadEventLog(pairing.channelId).catch(() => [])),
      }),
    );
    this.attachListener(pairing.channelId, client);
    return `listener:${pairing.channelId}`;
  }

  listDevices(): ManagerSnapshot['devices'] {
    return this.getSnapshot().devices;
  }

  terminal(channelId: string): TerminalController {
    let terminal = this.terminals.get(channelId);
    if (!terminal) {
      terminal = new TerminalController({
        capabilities: () => this.device(channelId)?.capabilities ?? [],
        send: async (message) => {
          const client = this.listenerController(channelId)?.client;
          if (!client || !this.device(channelId)?.connected) throw new Error('Device disconnected.');
          await client.send(message);
        },
      });
      terminal.setConnected(this.device(channelId)?.connected ?? false);
      this.terminals.set(channelId, terminal);
    }
    return terminal;
  }

  async connectDevice(channelId: string): Promise<void> {
    const device = this.device(channelId);
    if (!device) return;
    const ctrl = this.ensureListenerController(channelId);
    if (ctrl.reconnecting) return;
    // Bail only if we already hold a HEALTHY client. A stale client whose socket silently died must
    // be REPLACED, not treated as connected — the devtunnel/relay transport does NOT self-heal (see
    // shared/transport-relay.mjs; unlike supabase-js it never re-opens a dropped socket), so once it
    // drops the old `if (ctrl.client) return` guard short-circuited every watchdog/resume retry and
    // wedged the device in "reconnecting" forever. attachListener() closes the previous client, so
    // replacing it here is clean.
    if (ctrl.client && device.connected) return;
    this.terminals.get(channelId)?.setConnected(false);
    ctrl.reconnecting = true;
    this.store.dispatch(deviceProjectsLoadingSet({ channelId, loading: true, attempt: true }));
    try {
      // Reuse the phone's ORIGINAL keypair from registration (connectDeviceSession, mirroring
      // connectSession) rather than pairWithPublicKey's fresh-keypair full handshake — the
      // listener locks onto the first phone public key it sees per run and would otherwise
      // reject every reconnect as "a different phone".
      const client = await connectDeviceSession({
        channelId,
        peerPublicKeyB64: device.pub,
        publicKeyB64: device.publicKeyB64,
        privateKeyJwk: device.privateKeyJwk,
        deviceId: device.deviceId,
        transport: device.transport,
      });
      this.attachListener(channelId, client);
    } catch (err) {
      this.clearDeviceUtilities(channelId);
      this.store.dispatch(
        deviceErrorSet({ channelId, error: errMessage(err, 'Could not reach this listener device.'), connected: false }),
      );
    } finally {
      ctrl.reconnecting = false;
    }
  }

  async refreshProjects(channelId: string): Promise<void> {
    // Collapse overlapping refresh triggers into a single in-flight project_list_request. A single
    // reconnect fans out to boot auto-reconnect + watchdog self-heal + attachListener's trailing
    // refresh (+ a manual pull), and connectDevice short-circuits once healthy, so without this
    // guard every trigger sends a duplicate request on the same client (5 in ~1min observed). The
    // marker is set synchronously (before the awaited connectDevice) so concurrent callers dedupe;
    // it clears when the PROJECT_LIST reply lands (onListenerMessage), on a new attach
    // (attachListener), or after PROJECT_LIST_INFLIGHT_MS so a dropped reply can't wedge refreshes.
    if (this.projectListInflight.has(channelId)) return;
    const failSafe = setTimeout(() => this.projectListInflight.delete(channelId), PROJECT_LIST_INFLIGHT_MS);
    (failSafe as { unref?: () => void }).unref?.();
    this.projectListInflight.set(channelId, failSafe);
    try {
      await this.connectDevice(channelId);
      const ctrl = this.listenerController(channelId);
      if (!ctrl?.client) {
        this.clearProjectListInflight(channelId);
        return;
      }
      this.store.dispatch(deviceProjectsLoadingSet({ channelId, loading: true }));
      const message = projectListRequest();
      this.recordDeviceEvent(channelId, 'out', message);
      await ctrl.client.send(message);
    } catch (err) {
      this.clearProjectListInflight(channelId);
      this.clearDeviceUtilities(channelId);
      this.store.dispatch(
        deviceErrorSet({ channelId, error: errMessage(err, 'Could not request projects.'), connected: false }),
      );
    }
  }

  openDeviceClipboard(channelId: string): void {
    this.closeDeviceClipboard(channelId);
    if (this.device(channelId)) this.store.dispatch(deviceClipboardOpened(channelId));
  }

  closeDeviceClipboard(channelId: string): void {
    this.listenerController(channelId)?.clear('clipboard');
    this.store.dispatch(deviceClipboardCleared(channelId));
  }

  readDeviceClipboard(channelId: string): void {
    this.sendDeviceClipboard(channelId, 'read');
  }

  writeDeviceClipboard(channelId: string, text: string): void {
    this.sendDeviceClipboard(channelId, 'write', text);
  }

  private sendDeviceClipboard(channelId: string, operation: 'read' | 'write', text?: string): void {
    const device = this.device(channelId);
    if (!device?.clipboard || device.clipboard.pending) return;
    const requestId = crypto.randomUUID();
    this.store.dispatch(deviceClipboardStarted({ channelId, requestId, operation }));
    const ctrl = this.listenerController(channelId);
    const fail = (code: DeviceUtilityCode): void => {
      if (this.device(channelId)?.clipboard?.requestId !== requestId) return;
      ctrl?.clear('clipboard');
      this.store.dispatch(deviceClipboardResultReceived({
        channelId, result: clipboardResult(requestId, operation, code).msg,
      }));
    };
    if (!device.capabilities?.includes(DEVICE_CAPABILITY.CLIPBOARD_V1)) return fail('unsupported');
    if (!device.connected || !ctrl?.client) return fail('unavailable');
    if (operation === 'write') {
      const error = clipboardTextError(text);
      if (error) return fail(error);
    }
    const message = operation === 'read'
      ? clipboardRead(requestId)
      : clipboardWrite(requestId, text ?? '');
    this.recordDeviceEvent(channelId, 'out', message);
    ctrl.arm('clipboard', () => fail('timeout'), DEVICE_UTILITY_TIMEOUT_MS);
    void ctrl.client.send(message).catch(() => fail('unavailable'));
  }

  refreshDeviceKeepAwake(channelId: string): void {
    this.sendDeviceKeepAwake(channelId, 'status');
  }

  startDeviceKeepAwake(channelId: string, durationMs: number): void {
    this.sendDeviceKeepAwake(channelId, 'start', durationMs);
  }

  stopDeviceKeepAwake(channelId: string): void {
    this.sendDeviceKeepAwake(channelId, 'stop');
  }

  private sendDeviceKeepAwake(channelId: string, operation: 'status' | 'start' | 'stop', durationMs?: number): void {
    const device = this.device(channelId);
    if (!device || device.keepAwake?.pending) return;
    const requestId = crypto.randomUUID();
    this.store.dispatch(deviceKeepAwakeStarted({ channelId, requestId }));
    const ctrl = this.listenerController(channelId);
    const fail = (code: DeviceUtilityCode): void => {
      if (this.device(channelId)?.keepAwake?.requestId !== requestId) return;
      ctrl?.clear('keepAwake');
      this.store.dispatch(deviceKeepAwakeFailed({ channelId, requestId, code }));
    };
    if (!device.capabilities?.includes(DEVICE_CAPABILITY.KEEP_AWAKE_V1)) return fail('unsupported');
    if (!device.connected || !ctrl?.client) return fail('unavailable');
    const duration = normalizeKeepAwakeDuration(durationMs ?? NaN);
    if (operation === 'start' && duration === null) return fail('invalid-request');
    const status = device.keepAwake?.status;
    const leaseId = status?.active ? status.leaseId : null;
    if (operation === 'stop' && !leaseId) return fail('lease-mismatch');
    const message = operation === 'status'
      ? keepAwakeStatusRequest(requestId)
      : operation === 'stop'
        ? keepAwakeStop(requestId, leaseId ?? '')
        : keepAwakeStart(requestId, leaseId ?? crypto.randomUUID(), duration ?? NaN);
    this.recordDeviceEvent(channelId, 'out', message);
    ctrl.arm('keepAwake', () => fail('timeout'), DEVICE_UTILITY_TIMEOUT_MS);
    void ctrl.client.send(message).catch(() => fail('unavailable'));
  }

  private clearDeviceUtilities(channelId: string): void {
    const ctrl = this.listenerController(channelId);
    ctrl?.clear('clipboard');
    ctrl?.clear('keepAwake');
    this.store.dispatch(deviceUtilitiesCleared(channelId));
  }

  startDeviceMonitoring(channelId: string): void {
    const device = this.device(channelId);
    if (!device?.capabilities?.includes(DEVICE_MONITOR_CAPABILITY)) return;

    const existing = this.deviceMonitors.get(channelId);
    if (existing && this.clock() < existing.expiresAt) {
      void this.sendDeviceMonitorStart(channelId, existing.monitorId);
      return;
    }
    if (existing) {
      clearInterval(existing.renewal);
      this.deviceMonitors.delete(channelId);
    }
    const monitorId = crypto.randomUUID();
    const renewal = setInterval(() => {
      this.startDeviceMonitoring(channelId);
    }, DEVICE_MONITOR_RENEW_MS);
    (renewal as { unref?: () => void }).unref?.();
    this.deviceMonitors.set(channelId, {
      monitorId,
      expiresAt: this.clock() + DEVICE_MONITOR_LEASE_MS,
      renewal,
    });
    this.store.dispatch(deviceMonitoringStarted({ channelId, monitorId, startedAt: this.clock() }));
    void this.sendDeviceMonitorStart(channelId, monitorId);
  }

  stopDeviceMonitoring(channelId: string): void {
    const lease = this.deviceMonitors.get(channelId);
    if (!lease) return;
    clearInterval(lease.renewal);
    this.deviceMonitors.delete(channelId);
    this.deviceHealthCacheWrittenAt.delete(channelId);
    this.store.dispatch(deviceMonitoringStopped({ channelId, monitorId: lease.monitorId }));

    const ctrl = this.listenerController(channelId);
    if (!ctrl?.client) return;
    const message = deviceMonitorStop(lease.monitorId);
    this.recordDeviceEvent(channelId, 'out', message);
    void ctrl.client.send(message).catch(() => {
      // The station's lease is the cleanup fallback when a page-close stop cannot be delivered.
    });
  }

  private async sendDeviceMonitorStart(channelId: string, monitorId: string): Promise<void> {
    const lease = this.deviceMonitors.get(channelId);
    if (lease?.monitorId !== monitorId) return;
    await this.connectDevice(channelId);
    const ctrl = this.listenerController(channelId);
    if (!ctrl?.client || this.deviceMonitors.get(channelId)?.monitorId !== monitorId) {
      this.store.dispatch(
        deviceMonitoringFailed({ channelId, monitorId, error: 'Waiting for the laptop to reconnect.' }),
      );
      return;
    }
    const message = deviceMonitorStart(monitorId, DEVICE_MONITOR_INTERVAL_MS, DEVICE_MONITOR_LEASE_MS);
    this.recordDeviceEvent(channelId, 'out', message);
    try {
      await ctrl.client.send(message);
      const current = this.deviceMonitors.get(channelId);
      if (current?.monitorId === monitorId) {
        current.expiresAt = this.clock() + DEVICE_MONITOR_LEASE_MS;
      }
    } catch (err) {
      this.store.dispatch(
        deviceMonitoringFailed({
          channelId,
          monitorId,
          error: errMessage(err, 'Could not start device monitoring.'),
        }),
      );
    }
  }

  private clearProjectListInflight(channelId: string): void {
    const timer = this.projectListInflight.get(channelId);
    if (timer) clearTimeout(timer);
    this.projectListInflight.delete(channelId);
  }

  private async setPendingStage(
    pending: PendingSpawn,
    stage: PendingOperationStage,
    error?: string,
  ): Promise<void> {
    pending.stage = stage;
    this.store.dispatch(
      spawnStateSet({
        id: pending.tempId,
        patch: { stage, slow: this.clock() - pending.createdAt >= RESUME_SLOW_MS },
      }),
    );
    this.store.dispatch(
      statusSet({
        id: pending.tempId,
        status: error ? 'error' : 'initializing',
        error,
      }),
    );
    await upsertPendingOperation(persistedPending(pending));
  }

  private armLaunchResponseTimer(pending: PendingSpawn): void {
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = setTimeout(() => {
      pending.timer = undefined;
      void this.setPendingStage(
        pending,
        pending.stage,
        `Devbox has not answered yet. Try again when it is online; Weft will reuse this launch request.`,
      );
    }, pending.kind === 'resume' ? RESUME_TIMEOUT_MS : SPAWN_DELIVERY_TIMEOUT_MS);
  }

  private async sendPendingRequest(pending: PendingSpawn): Promise<void> {
    await this.connectDevice(pending.deviceId);
    const ctrl = this.listenerController(pending.deviceId);
    if (!ctrl?.client) throw new Error('Listener device is not connected.');
    const device = this.device(pending.deviceId);
    const lifecycleCapable = device?.capabilities?.includes(DEVICE_CAPABILITY.SESSION_ACTIVATION_V1);
    const message = lifecycleCapable
      ? openSessionMessage(
          pending.requestId,
          pending.kind === 'resume'
            ? {
                kind: 'existing',
                storeAuthority: pending.storeAuthority ?? null,
                sessionId: pending.sessionId ?? '',
              }
            : { kind: 'new', projectName: pending.projectName },
          {
            mode: pending.mode,
            name: pending.name ?? null,
            takeoverRequested: pending.force === true,
          },
        )
      : pending.kind === 'resume'
        ? resumeSessionMessage(
            pending.requestId,
            pending.sessionId ?? '',
            pending.mode,
            pending.force === true,
          )
        : spawnSessionMessage(
            pending.requestId,
            pending.projectName,
            pending.mode,
            pending.name ?? null,
          );
    this.recordDeviceEvent(pending.deviceId, 'out', message);
    await ctrl.client.send(message);
    await this.setPendingStage(pending, 'delivered');
    this.armLaunchResponseTimer(pending);
  }

  private async sendLifecycleCommand(
    channelId: string,
    operationId: string,
    afterRevision: number,
    message: EventEnvelope,
  ): Promise<LifecycleStatusMsg> {
    await this.connectDevice(channelId);
    const ctrl = this.listenerController(channelId);
    if (!ctrl?.client) throw new Error('Listener device is not connected.');
    const response = new Promise<LifecycleStatusMsg>((resolve, reject) => {
      const waiter: LifecycleWaiter = {
        afterRevision,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.lifecycleWaiters.get(operationId)?.delete(waiter);
          reject(new Error('The laptop did not acknowledge the lifecycle command.'));
        }, SPAWN_DELIVERY_TIMEOUT_MS),
      };
      const waiters = this.lifecycleWaiters.get(operationId) ?? new Set<LifecycleWaiter>();
      waiters.add(waiter);
      this.lifecycleWaiters.set(operationId, waiters);
    });
    this.recordDeviceEvent(channelId, 'out', message);
    try {
      await ctrl.client.send(message);
    } catch (error) {
      const waiters = this.lifecycleWaiters.get(operationId);
      for (const waiter of waiters ?? []) {
        clearTimeout(waiter.timer);
        waiter.reject(error instanceof Error ? error : new Error(String(error)));
      }
      this.lifecycleWaiters.delete(operationId);
      throw error;
    }
    return response;
  }

  private async attemptPendingPair(requestId: string): Promise<void> {
    const pending = this.pendingSpawns.get(requestId);
    if (!pending?.pairingPayload || pending.pairingInFlight) return;
    pending.pairingInFlight = true;
    clearSpawnTimers(pending);
    try {
      pending.phoneIdentity ??= await createPhonePairingIdentity();
      await this.setPendingStage(pending, 'pairing');
      const { client, pairing } = await pairSession(pending.pairingPayload, {
        identity: pending.phoneIdentity as PhonePairingIdentity,
        timeoutMs: PAIRING_ATTEMPT_TIMEOUT_MS,
      });
      const title = pending.name || pending.title || pending.projectName;
      const oldActive = this.activeId();
      await this.openPairedSession(client, pairing, {
        title,
        activate: true,
        renamed: Boolean(pending.name),
        spawnedFromDeviceId: pending.spawnedFromDeviceId,
        spawnedFromDeviceName: pending.spawnedFromDeviceName,
      });
      const listener = this.listenerController(pending.deviceId)?.client;
      if (listener) {
        const claimed = sessionClaimedMessage(pairing.channelId, pending.requestId);
        this.recordDeviceEvent(pending.deviceId, 'out', claimed);
        await listener.send(claimed).catch(() => {});
      }
      this.pendingSpawns.delete(requestId);
      await removePendingOperation(requestId);
      this.store.dispatch(sessionRemoved(pending.tempId));
      if (oldActive === pending.tempId) this.store.dispatch(sessionActivated(pairing.channelId));
    } catch (err) {
      await this.setPendingStage(
        pending,
        'pairing-failed',
        errMessage(
          err,
          'The session started, but the phone could not connect yet. Try again to reconnect to it.',
        ),
      );
    } finally {
      pending.pairingInFlight = false;
    }
  }

  async spawnSession(channelId: string, opts: SpawnRequestOptions): Promise<string> {
    const device = this.device(channelId);
    if (!device) throw new Error('Choose a registered listener device first.');
    const requestId = opts.operationId ?? `spawn-${crypto.randomUUID()}`;
    const tempId = `initializing-${requestId}`;
    const displayName = opts.name?.trim() || opts.projectName;
    const createdAt = this.clock();
    const meta: SessionMeta = {
      channelId: tempId,
      title: displayName,
      cwd: null,
      kind: 'spawning',
      addedAt: createdAt,
      spawnedFromDeviceId: device.deviceId ?? channelId,
      spawnedFromDeviceName: device.name,
    };
    const session = emptySession(tempId, meta);
    session.connection.status = 'initializing';
    session.connection.spawning = {
      requestId,
      deviceId: channelId,
      deviceName: device.name,
      projectName: opts.projectName,
      operation: 'new',
      stage: 'not-delivered',
      createdAt,
      mode: opts.mode,
    };
    this.store.dispatch(sessionAdded(session));
    this.store.dispatch(sessionActivated(tempId));

    const pending: PendingSpawn = {
      requestId,
      tempId,
      deviceId: channelId,
      spawnedFromDeviceId: device.deviceId ?? channelId,
      spawnedFromDeviceName: device.name,
      kind: 'new',
      projectName: opts.projectName,
      name: opts.name?.trim() || undefined,
      mode: opts.mode,
      createdAt,
      stage: 'not-delivered',
    };
    pending.slowTimer = setTimeout(() => {
      if (!this.pendingSpawns.has(requestId)) return;
      this.store.dispatch(spawnSlowSet({ id: tempId }));
    }, RESUME_SLOW_MS);
    this.pendingSpawns.set(requestId, pending);
    await upsertPendingOperation(persistedPending(pending));

    try {
      await this.sendPendingRequest(pending);
      this.store.dispatch(deviceLastProjectSet({ channelId, projectName: opts.projectName }));
      void patchDevice(channelId, { lastProjectName: opts.projectName });
    } catch (err) {
      await this.setPendingStage(
        pending,
        'not-delivered',
        errMessage(err, 'Could not reach Devbox. Try again when it is online.'),
      );
    }

    return tempId;
  }

  /** On-demand pull of the laptop's recent resumable CLI sessions (SESSION_LIST_REQUEST). Lazy — the
   *  Device details "Resume a session" section calls this on mount and on manual refresh, never on
   *  bind, because the session store is large and rewritten every turn. The reply lands in
   *  onListenerMessage → deviceSessionsReceived. Best-effort: a connect/send failure just clears the
   *  loading flag (the section shows whatever was last pulled) without wedging the device. */
  async refreshSessions(channelId: string, cwd?: string | null): Promise<void> {
    this.store.dispatch(deviceSessionsLoadingSet({ channelId, loading: true }));
    // Comms are async: the SESSION_LIST reply arrives later via onListenerMessage (or never, if the
    // laptop is wedged). Arm a failsafe so the button can't stay stuck on "Loading…" — clearing the
    // flag is idempotent, so if the reply lands first (deviceSessionsReceived also clears it) this is
    // a harmless no-op.
    const failSafe = setTimeout(
      () => this.store.dispatch(deviceSessionsLoadingSet({ channelId, loading: false })),
      SESSION_LIST_INFLIGHT_MS,
    );
    (failSafe as { unref?: () => void }).unref?.();
    try {
      await this.connectDevice(channelId);
      const ctrl = this.listenerController(channelId);
      if (!ctrl?.client) {
        clearTimeout(failSafe);
        this.store.dispatch(deviceSessionsLoadingSet({ channelId, loading: false }));
        return;
      }
      const message = sessionListRequest(null, cwd ?? null);
      this.recordDeviceEvent(channelId, 'out', message);
      await ctrl.client.send(message);
    } catch (err) {
      clearTimeout(failSafe);
      this.store.dispatch(deviceSessionsLoadingSet({ channelId, loading: false }));
      this.store.dispatch(
        deviceErrorSet({ channelId, error: errMessage(err, 'Could not request sessions.'), connected: false }),
      );
    }
  }

  /** Resume an existing CLI session from the laptop's store: spawn `copilot --resume=<id>` in that
   *  session's own cwd and pair to it — reusing the entire spawn machinery (pendingSpawns →
   *  SPAWN_PAIRING → openPairedSession), so the only difference from `spawnSession` is the wire
   *  message (RESUME_SESSION vs SPAWN_SESSION) and that there's no project. The placeholder card is
   *  titled from the tapped session; `renamed` stays false so the resumed session's own CLI title
   *  (re-reported via session_meta once paired) wins. Returns the temp id of the Initializing card. */
  async resumeSession(channelId: string, opts: ResumeRequestOptions): Promise<string> {
    const device = this.device(channelId);
    if (!device) throw new Error('Choose a registered listener device first.');

    // Resume forks a fresh `copilot --resume` on a brand-new channel, so tapping a row we are
    // already driving would leave two CLI processes writing one session-store entry — and the
    // reconcile that follows disposes the duplicate *card* while the orphaned process runs on.
    // Prefer what we already have. (Cards are keyed by channel, so the only way to recognise the
    // same CLI session is meta.sessionId, which live sessions report via session_meta.)
    //
    // A `spawning` card is deliberately excluded once it has failed. It carries the sessionId but
    // never became a real attachment and was never persisted, so matching it here made one timed-out
    // resume poison the row for good: the next tap found the dead placeholder, tried to reconnect a
    // channel the laptop has no record of, and was told the session was no longer saved. The only
    // escape was Dismiss, which nothing on screen suggested. A resume still in flight is different —
    // that one we honour, so a double tap doesn't launch two processes.
    const existing = opts.sessionId
      ? selectAllSessions(this.store.getState()).find(
          (s) =>
            s.meta.sessionId === opts.sessionId &&
            s.connection.status !== 'ended' &&
            (s.meta.kind !== 'spawning' || s.connection.status === 'initializing'),
        )
      : undefined;
    if (existing) {
      const existingId = existing.meta.channelId;
      this.store.dispatch(sessionActivated(existingId));
      const status = existing.connection.status;
      // Already attached, or on its way: opening it is the whole of what the user asked for. A cold
      // card is excluded even though it reads as `idle` — archiving releases the socket, so idle
      // there means "deliberately parked", not "connected and quiet".
      if (
        !existing.connection.cold &&
        (status === 'live' || status === 'idle' || status === 'connecting' || status === 'initializing')
      ) {
        return existingId;
      }
      // Archived (cold) or Offline (error). The pairing we hold may well still be good — the laptop
      // was merely asleep, or the app was closed past the archive window. Reconnecting is cheaper
      // than a resume, keeps the transcript attached to the same card, and cannot fork a process.
      // Only when it fails to come back do we fall through and genuinely resume.
      if (await this.reconnectAndConfirm(existingId)) return existingId;
    }

    const requestId = opts.operationId ?? `resume-${crypto.randomUUID()}`;
    const tempId = `initializing-${requestId}`;
    const displayName = opts.title?.trim() || (opts.cwd ? basename(opts.cwd) : '') || 'Resuming session';
    const createdAt = this.clock();
    const meta: SessionMeta = {
      channelId: tempId,
      sessionId: opts.sessionId,
      title: displayName,
      cwd: opts.cwd ?? null,
      kind: 'spawning',
      addedAt: createdAt,
      spawnedFromDeviceId: device.deviceId ?? channelId,
      spawnedFromDeviceName: device.name,
    };
    const session = emptySession(tempId, meta);
    session.connection.status = 'initializing';
    session.connection.spawning = {
      requestId,
      deviceId: channelId,
      deviceName: device.name,
      projectName: displayName,
      operation: 'resume',
      stage: 'not-delivered',
      createdAt,
      mode: opts.mode,
    };
    this.store.dispatch(sessionAdded(session));
    this.store.dispatch(sessionActivated(tempId));

    const pending: PendingSpawn = {
      requestId,
      tempId,
      deviceId: channelId,
      spawnedFromDeviceId: device.deviceId ?? channelId,
      spawnedFromDeviceName: device.name,
      kind: 'resume',
      // Used only as the Initializing placeholder title fallback; `name` is left undefined so
      // handleSpawnPairing keeps renamed=false and the resumed session's own CLI title wins.
      projectName: displayName,
      mode: opts.mode,
      sessionId: opts.sessionId,
      storeAuthority: opts.storeAuthority ?? null,
      cwd: opts.cwd ?? undefined,
      title: displayName,
      force: opts.force === true,
      createdAt,
      stage: 'not-delivered',
      // Past the point a fresh spawn would have landed, say so rather than sitting mute. The card
      // stays `initializing`, so this is a note and not a failure.
      slowTimer: setTimeout(() => {
        if (!this.pendingSpawns.has(requestId)) return;
        this.store.dispatch(spawnSlowSet({ id: tempId }));
      }, RESUME_SLOW_MS),
    };
    this.pendingSpawns.set(requestId, pending);
    await upsertPendingOperation(persistedPending(pending));

    try {
      await this.sendPendingRequest(pending);
    } catch (err) {
      await this.setPendingStage(
        pending,
        'not-delivered',
        errMessage(err, 'Could not reach Devbox. Try again when it is online.'),
      );
    }

    return tempId;
  }

  /** Recover the operation behind a failed placeholder. Pairing retries reuse the original channel
   *  and phone key; delivery retries reuse the original requestId so a capable Device Station can
   *  replay the launch instead of opening another terminal. Only a stale New operation may fall back
   *  to a replacement; Resume never duplicates a session-store writer. */
  async retrySpawn(channelId: string): Promise<string | null> {
    const session = this.session(channelId);
    const spawning = session?.connection.spawning;
    if (!session || !spawning || session.meta.kind !== 'spawning') return null;
    const pending = this.pendingSpawns.get(spawning.requestId);
    if (!pending) return null;
    this.store.dispatch(statusSet({ id: pending.tempId, status: 'initializing', error: undefined }));
    if (pending.pairingPayload) {
      await this.attemptPendingPair(pending.requestId);
      if (
        pending.kind === 'new' &&
        pending.stage === 'pairing-failed' &&
        this.clock() - pending.createdAt >= STALE_LAUNCH_MS
      ) {
        const replacement = {
          deviceId: pending.deviceId,
          projectName: pending.projectName,
          mode: pending.mode,
          name: pending.name,
        };
        await this.remove(channelId);
        return this.spawnSession(replacement.deviceId, replacement);
      }
      return pending.tempId;
    }
    try {
      await this.sendPendingRequest(pending);
    } catch (err) {
      await this.setPendingStage(
        pending,
        'not-delivered',
        errMessage(err, 'Could not reach Devbox. Try again when it is online.'),
      );
    }
    return pending.tempId;
  }

  async forgetDevice(channelId: string): Promise<void> {
    this.terminals.get(channelId)?.dispose();
    this.terminals.delete(channelId);
    this.clearDeviceUtilities(channelId);
    this.stopDeviceMonitoring(channelId);
    const ctrl = this.listenerController(channelId);
    if (ctrl?.client) {
      const message = forgetDeviceMessage();
      this.recordDeviceEvent(channelId, 'out', message);
      await ctrl.client.send(message).catch(() => {});
    }
    ctrl?.dispose();
    this.listenerControllers.delete(channelId);
    for (const pending of [...this.pendingSpawns.values()]) {
      if (pending.deviceId === channelId) this.failSpawn(pending.requestId, 'Listener device was forgotten.');
    }
    await removeDevice(channelId);
    await clearEventLog(channelId).catch(() => {});
    this.store.dispatch(deviceRemoved(channelId));
  }

  async setDefaultDevice(channelId: string): Promise<void> {
    await persistDefaultDevice(channelId);
    this.store.dispatch(deviceDefaultSet(channelId));
  }

  private attachListener(channelId: string, client: WeftClient): void {
    const ctrl = this.ensureListenerController(channelId);
    this.clearDeviceUtilities(channelId);
    const previous = ctrl.client;
    ctrl.detach();
    ctrl.client = client;
    if (previous && previous !== client) void previous.close().catch(() => {});
    const stopEvents = client.subscribe((message) => this.onListenerMessage(channelId, client, message));
    const stopStatus = client.onStatus((status) => {
      if (status !== 'connected') this.clearDeviceUtilities(channelId);
      this.store.dispatch(deviceErrorSet({ channelId, connected: status === 'connected' }));
      this.terminals.get(channelId)?.setConnected(status === 'connected');
      if (status === 'connected') void this.refreshProjects(channelId);
    });
    ctrl.unsubscribe = () => {
      stopEvents();
      stopStatus();
    };
    this.store.dispatch(deviceErrorSet({ channelId, error: undefined, connected: true }));
    this.terminals.get(channelId)?.setConnected(true);
    // Fresh client: drop any stale in-flight marker from the previous (now-closed) socket so this
    // genuine (re)attach always issues exactly one project_list_request.
    this.clearProjectListInflight(channelId);
    void this.refreshProjects(channelId);
  }

  private async reconcileDevice(channelId: string, deviceId: string): Promise<void> {
    try {
      const { removedChannelIds, merged } = await reconcileDeviceId(channelId, deviceId);
      this.store.dispatch(deviceReconciled({ channelId, removedChannelIds, merged }));
      for (const dead of removedChannelIds) {
        this.terminals.get(dead)?.dispose();
        this.terminals.delete(dead);
        this.clearDeviceUtilities(dead);
        this.stopDeviceMonitoring(dead);
        this.clearProjectListInflight(dead);
        this.listenerController(dead)?.dispose();
        this.listenerControllers.delete(dead);
      }
    } catch {
      // Best-effort: a failed reconcile just means a stale duplicate lingers until the next one.
    }
  }

  private onListenerMessage(channelId: string, client: WeftClient, message: EventEnvelope): void {
    const ctrl = this.listenerController(channelId);
    if (!ctrl || ctrl.client !== client || message.eventType !== EVENT_TYPE.CONTROL) return;
    if (isTerminalEnvelope(message)) {
      this.store.dispatch(deviceSeen({ channelId }));
      this.terminals.get(channelId)?.receive(message);
      return;
    }
    this.recordDeviceEvent(channelId, 'in', message);
    // Single inbound choke point: ANY control message is proof the laptop is alive right now, so
    // refresh "last seen" here — before the per-subtype branches — so SPAWN_PAIRING, SPAWN_RESULT
    // and any future control message keep the device fresh, not just the state-carrying subtypes.
    this.store.dispatch(deviceSeen({ channelId }));
    if (message.eventSubtype === SUBTYPE.CONTROL.DEVICE_HEARTBEAT) {
      // Proactive liveness beat (independent of PROJECT_LIST request/reply). deviceSeen above
      // already refreshed lastSeenAt/connected + cleared error, so there's nothing else to do —
      // the beat exists purely to keep an idle device from going stale between polls.
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.CLIPBOARD_RESULT) {
      const result = message.msg;
      const clipboard = this.device(channelId)?.clipboard;
      if (!clipboard?.pending || result.requestId !== clipboard.requestId || result.operation !== clipboard.operation) return;
      this.listenerController(channelId)?.clear('clipboard');
      this.store.dispatch(deviceClipboardResultReceived({
        channelId, result: clipboardResult(result.requestId, result.operation, result.code, result.text).msg,
      }));
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.KEEP_AWAKE_STATUS) {
      const status = message.msg;
      if (
        !Number.isSafeInteger(status.revision) || status.revision < 0 ||
        typeof status.active !== 'boolean' ||
        (status.requestId !== null && typeof status.requestId !== 'string') ||
        (status.leaseId !== null && typeof status.leaseId !== 'string') ||
        (status.active && (!status.leaseId || !Number.isFinite(status.expiresAt))) ||
        !DEVICE_UTILITY_CODES.includes(status.code)
      ) return;
      if (status.requestId !== null && status.requestId === this.device(channelId)?.keepAwake?.requestId) {
        this.listenerController(channelId)?.clear('keepAwake');
      }
      this.store.dispatch(deviceKeepAwakeStatusReceived({
        channelId, status: keepAwakeStatus(status.requestId, status).msg,
      }));
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.PROJECT_LIST) {
      const msg = message.msg as ProjectListMsg;
      this.clearProjectListInflight(channelId);
      if (!msg.capabilities?.includes(DEVICE_CAPABILITY.CLIPBOARD_V1)) ctrl.clear('clipboard');
      if (!msg.capabilities?.includes(DEVICE_CAPABILITY.KEEP_AWAKE_V1)) ctrl.clear('keepAwake');
      this.store.dispatch(
        deviceProjectsReceived({
          channelId,
          projects: msg.projects ?? [],
          capabilities: msg.capabilities ?? [],
          deviceName: msg.deviceName,
          defaultPermissionMode: msg.defaultPermissionMode ?? 'default',
        }),
      );
      if (msg.capabilities?.includes(DEVICE_CAPABILITY.KEEP_AWAKE_V1)) this.refreshDeviceKeepAwake(channelId);
      if (msg.deviceName) void patchDevice(channelId, { name: msg.deviceName });
      // The listener's deviceId is stable across `weft start` restarts even though this
      // channelId is a fresh ephemeral pairing channel (forward secrecy). Fold any stale entry
      // for the same physical laptop into this one instead of leaving a dead duplicate around.
      if (msg.deviceId) void this.reconcileDevice(channelId, msg.deviceId);
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.DEVICE_SNAPSHOT) {
      const snapshot = message.msg as DeviceSnapshotMsg;
      if (
        snapshot.schemaVersion !== 1 ||
        typeof snapshot.monitorId !== 'string' ||
        !Number.isInteger(snapshot.sequence) ||
        snapshot.sequence < 0
      ) {
        return;
      }
      const monitoring = this.store.getState().sessions.devices
        .find((device) => device.channelId === channelId)?.monitoring;
      if (
        !monitoring ||
        snapshot.monitorId !== monitoring.monitorId ||
        snapshot.sequence <= monitoring.latestSequence
      ) {
        return;
      }
      this.store.dispatch(
        deviceSnapshotReceived({ channelId, snapshot }),
      );
      const cachedHealth: DeviceHealthCache = {
        capturedAt: snapshot.capturedAt,
        effectiveIntervalMs: snapshot.effectiveIntervalMs ?? DEVICE_MONITOR_INTERVAL_MS,
        system: snapshot.system,
        issues: snapshot.issues.filter((issue) => issue.component !== 'apps'),
      };
      this.store.dispatch(deviceHealthCached({ channelId, cachedHealth }));
      const lastCacheWrite = this.deviceHealthCacheWrittenAt.get(channelId);
      if (lastCacheWrite === undefined || this.clock() - lastCacheWrite >= DEVICE_HEALTH_CACHE_WRITE_MS) {
        this.deviceHealthCacheWrittenAt.set(channelId, this.clock());
        void patchDevice(channelId, { cachedHealth });
      }
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.SPAWN_PAIRING) {
      void this.handleSpawnPairing(message.msg as SpawnPairingMsg);
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.LAUNCH_STATUS) {
      void this.handleLaunchStatus(message.msg as LaunchStatusMsg);
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.LIFECYCLE_STATUS) {
      void this.handleLifecycleStatus(message.msg as LifecycleStatusMsg);
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.SESSION_OFFERS) {
      const msg = message.msg as SessionOffersMsg;
      const offers = (msg.offers ?? []).filter((o) => o && typeof o.channelId === 'string' && o.payload);
      this.store.dispatch(deviceSessionOffersReceived({ channelId, offers }));
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.SESSION_LIST) {
      const msg = message.msg as SessionListMsg;
      const sessions = (msg.sessions ?? []).filter(
        (s) => s && typeof s.sessionId === 'string' && s.sessionId && typeof s.cwd === 'string' && s.cwd,
      );
      this.store.dispatch(deviceSessionsReceived({ channelId, sessions, folders: msg.folders }));
      return;
    }
    if (message.eventSubtype === SUBTYPE.CONTROL.SPAWN_RESULT) {
      const msg = message.msg as SpawnResultMsg;
      const pending = this.pendingSpawns.get(msg.requestId);
      if (!pending) return;
      if (msg.ok) {
        clearSpawnTimers(pending);
        pending.timer = undefined;
        void this.setPendingStage(pending, 'launched');
      } else {
        void this.failSpawn(msg.requestId, msg.error || 'The laptop could not start the session.');
      }
    }
  }

  private async handleLaunchStatus(msg: LaunchStatusMsg): Promise<void> {
    const pending = this.pendingSpawns.get(msg.requestId);
    if (!pending) return;
    if (msg.name) pending.name = msg.name;

    switch (msg.state) {
      case 'accepted':
        await this.setPendingStage(pending, 'delivered');
        return;
      case 'launched':
        clearSpawnTimers(pending);
        pending.timer = undefined;
        await this.setPendingStage(pending, 'launched');
        return;
      case 'ready':
        clearSpawnTimers(pending);
        pending.timer = undefined;
        pending.slowTimer = undefined;
        if (msg.payload) pending.pairingPayload = msg.payload;
        await this.setPendingStage(pending, msg.payload ? 'ready' : 'launched');
        if (pending.pairingPayload) await this.attemptPendingPair(msg.requestId);
        return;
      case 'failed':
        await this.failSpawn(msg.requestId, msg.error || 'The laptop could not start the session.');
        return;
      case 'claimed': {
        clearSpawnTimers(pending);
        this.pendingSpawns.delete(msg.requestId);
        await removePendingOperation(msg.requestId);
        this.store.dispatch(sessionRemoved(pending.tempId));
        const claimedChannelId = msg.payload?.channelId;
        if (claimedChannelId && this.session(claimedChannelId)) {
          this.store.dispatch(sessionActivated(claimedChannelId));
        }
        return;
      }
      case 'abandoned':
      case 'superseded':
        await this.failSpawn(
          msg.requestId,
          msg.error || `This ${pending.kind === 'resume' ? 'resume' : 'launch'} was ${msg.state}.`,
        );
    }
  }

  private async handleLifecycleStatus(msg: LifecycleStatusMsg): Promise<void> {
    if (
      typeof msg.operationId !== 'string' ||
      !msg.operationId ||
      !Number.isSafeInteger(msg.revision) ||
      msg.revision < 0
    ) return;
    const previous = this.lifecycleStatuses.get(msg.operationId);
    if (previous && msg.revision < previous.revision) return;
    this.lifecycleStatuses.set(msg.operationId, msg);
    const waiters = this.lifecycleWaiters.get(msg.operationId);
    if (waiters) {
      for (const waiter of [...waiters]) {
        if (msg.revision > waiter.afterRevision || msg.failure?.code === 'operation-stale') {
          clearTimeout(waiter.timer);
          waiters.delete(waiter);
          waiter.resolve(msg);
        }
      }
      if (waiters.size === 0) this.lifecycleWaiters.delete(msg.operationId);
    }
    const pending = this.pendingSpawns.get(msg.operationId);
    if (!pending) return;
    pending.lifecycleStatus = msg;
    await upsertPendingOperation(persistedPending(pending));
    if (msg.name) pending.name = msg.name;
    switch (msg.state) {
      case 'accepted':
      case 'locating':
      case 'reserved':
        await this.setPendingStage(pending, 'delivered');
        return;
      case 'activating':
      case 'launching':
        clearSpawnTimers(pending);
        pending.timer = undefined;
        await this.setPendingStage(pending, 'launched');
        return;
      case 'pairing-ready':
      case 'pairing':
        clearSpawnTimers(pending);
        pending.timer = undefined;
        pending.slowTimer = undefined;
        if (msg.payload) pending.pairingPayload = msg.payload;
        await this.setPendingStage(pending, msg.payload ? 'ready' : 'launched');
        if (pending.pairingPayload) await this.attemptPendingPair(msg.operationId);
        return;
      case 'open':
        clearSpawnTimers(pending);
        if (msg.payload && !pending.pairingPayload) {
          pending.pairingPayload = msg.payload;
          await this.setPendingStage(pending, 'ready');
          await this.attemptPendingPair(msg.operationId);
          return;
        }
        this.pendingSpawns.delete(msg.operationId);
        await removePendingOperation(msg.operationId);
        this.store.dispatch(sessionRemoved(pending.tempId));
        return;
      case 'failed':
        await this.failSpawn(
          msg.operationId,
          msg.failure?.message || 'The laptop could not open the session.',
        );
        return;
      case 'cancelled':
        await this.failSpawn(
          msg.operationId,
          msg.failure?.message || 'The session operation was cancelled.',
        );
    }
  }

  private async handleSpawnPairing(msg: SpawnPairingMsg): Promise<void> {
    const pending = this.pendingSpawns.get(msg.requestId);
    if (!pending) return;
    clearSpawnTimers(pending);
    pending.timer = undefined;
    pending.slowTimer = undefined;
    pending.pairingPayload = msg.payload as PairingPayload;
    if (msg.name) pending.name = msg.name;
    await this.setPendingStage(pending, 'launched');
    await this.attemptPendingPair(msg.requestId);
  }

  private async failSpawn(requestId: string, error: string): Promise<void> {
    const pending = this.pendingSpawns.get(requestId);
    if (!pending) return;
    clearSpawnTimers(pending);
    this.pendingSpawns.delete(requestId);
    const session = this.session(pending.tempId);
    if (session) this.store.dispatch(statusSet({ id: pending.tempId, status: 'error', error }));
    await removePendingOperation(requestId);
  }

  /** Adopt an in-session `/weft` offer relayed by a Device Station (the mirror of a spawned session):
   *  pair digitally to the offered session's own channel, open it, then tell the station we claimed it
   *  (SESSION_CLAIMED) so it stops advertising the offer. Attributed to the offering laptop like a
   *  spawned session. Best-effort claim: even if the notify send fails, the station drops the offer
   *  on its owning session's own withdraw (pair) so it won't linger. Returns the joined channelId.
   *  Throws (surfaced to the caller/UI) if pairing fails so the tapped offer can be retried. */
  async joinOfferedSession(deviceChannelId: string, offerChannelId: string): Promise<string> {
    const device = this.store.getState().sessions.devices.find((d) => d.channelId === deviceChannelId);
    const offer = device?.offers?.find((o) => o.channelId === offerChannelId);
    if (!offer || !offer.payload) throw new Error('This session offer is no longer available.');
    // Optimistically hide the offer so a double-tap can't start two pairings; a later SESSION_OFFERS
    // refresh (which excludes claimed ids) keeps it gone.
    this.store.dispatch(deviceOfferRemoved({ channelId: deviceChannelId, offerChannelId }));
    try {
      const payload = offer.payload as PairingPayload;
      const { client, pairing } = await pairSession(payload);
      const joinedId = await this.openPairedSession(client, pairing, {
        title: offer.name || null,
        cwd: offer.cwd ?? null,
        activate: true,
        renamed: Boolean(offer.name),
        spawnedFromDeviceId: device?.deviceId ?? deviceChannelId,
        spawnedFromDeviceName: device?.name ?? undefined,
      });
      this.notifyOfferClaimed(deviceChannelId, offerChannelId);
      return joinedId;
    } catch (err) {
      // Pairing failed — the offer is (probably) still live on the laptop, so a subsequent
      // SESSION_OFFERS re-broadcast will bring it back for another attempt.
      throw new Error(errMessage(err, 'Could not join the offered session.'));
    }
  }

  /** Best-effort SESSION_CLAIMED notify to the offering station over its device channel. */
  private notifyOfferClaimed(deviceChannelId: string, offerChannelId: string): void {
    const ctrl = this.listenerController(deviceChannelId);
    if (!ctrl?.client) return;
    const message = sessionClaimedMessage(offerChannelId);
    this.recordDeviceEvent(deviceChannelId, 'out', message);
    void ctrl.client.send(message).catch(() => {
      // The owning session withdraws its own offer entry on pair, so the station stops advertising
      // it regardless; this notify is just a faster path.
    });
  }

  // --- session controls ----------------------------------------------------------------------------
  /** Ask the laptop for recent turns again, on purpose. The automatic request can be lost to a
   *  race on connect, and while `channel_up` now retries it there is no way to recover from a
   *  laptop that was simply busy — so the thread menu gets an explicit escape hatch. */
  reloadHistory(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || !ctrl.client || ctrl.ephemeral) return;
    this.syncHistory(channelId, ctrl.client);
  }

  /** Rename a session to a phone-local label (#37). Repeated reports of the same Copilot title keep
   *  the label; a laptop `/rename` takes ownership. A blank label restores the latest Copilot title. */
  renameSession(channelId: string, rawTitle: string): void {
    const session = this.session(channelId);
    if (!session) return;
    const ctrl = this.controllers.get(channelId);
    const title = rawTitle.trim();
    if (!title) {
      const fallback = titleFor(channelId, session.meta.cwd, session.meta.reportedTitle ?? null);
      this.store.dispatch(titleSet({ id: channelId, title: fallback, renamed: false }));
      if (!ctrl?.ephemeral)
        void patchSession(channelId, { title: session.meta.reportedTitle ?? null, renamed: false });
      return;
    }
    this.store.dispatch(titleSet({ id: channelId, title, renamed: true }));
    if (!ctrl?.ephemeral) void patchSession(channelId, { title, renamed: true });
  }

  setActive(channelId: string): void {
    const session = this.session(channelId);
    if (!session) return;
    const ctrl = this.controllers.get(channelId);
    if (this.activeId() === channelId) {
      if (session.unread) {
        this.store.dispatch(unreadSet({ id: channelId, on: false }));
        if (!ctrl?.ephemeral) void patchSession(channelId, { unread: false, unreadCount: 0 });
      }
      return;
    }
    this.store.dispatch(sessionActivated(channelId));
    this.touchWarm(channelId);
    this.ensureConnected(channelId);
    if (!ctrl?.ephemeral) {
      const lastSeenAt = this.clock();
      void (async () => {
        await setLastActiveSessionId(channelId);
        await patchSession(channelId, { lastSeenAt, unread: false, unreadCount: 0 });
      })();
    }
  }

  async remove(channelId: string): Promise<void> {
    const demoDevice = this.demoDevices.get(channelId);
    if (demoDevice) {
      this.terminals.get(demoDevice)?.dispose();
      this.terminals.delete(demoDevice);
      this.listenerControllers.get(demoDevice)?.dispose();
      this.listenerControllers.delete(demoDevice);
      this.store.dispatch(deviceRemoved(demoDevice));
      this.demoDevices.delete(channelId);
    }
    const session = this.session(channelId);
    if (!session) return;
    for (const pending of [...this.pendingSpawns.values()]) {
      if (pending.tempId === channelId) {
        clearSpawnTimers(pending);
        this.pendingSpawns.delete(pending.requestId);
        await removePendingOperation(pending.requestId);
      }
    }
    const ctrl = this.controllers.get(channelId);
    const wasActive = this.activeId() === channelId;
    discardTranscriptWrites(channelId);
    if (ctrl) {
      ctrl.dispose();
      this.controllers.delete(channelId);
    }
    this.registry.dispose(channelId);
    this.warmLru = this.warmLru.filter((id) => id !== channelId);
    if (!ctrl?.ephemeral) {
      await removeSession(channelId);
      await clearTranscript(channelId);
      await clearEventLog(channelId);
    }
    this.store.dispatch(sessionRemoved(channelId));
    this.lastLivenessWriteAt.delete(channelId);
    this.lastKnownHeartbeat.delete(channelId);
    if (wasActive) {
      const nextId = (this.store.getState().sessions.ids[0] as string | undefined) ?? null;
      if (nextId) {
        this.store.dispatch(sessionActivated(nextId));
        this.ensureConnected(nextId);
      }
    }
  }

  /** Pin or unpin a session (#163): pinned cards are exempt from the 2-day auto-delete sweep and are
   *  the last picked for warm-pool eviction. Persisted so the choice survives reload. */
  async pin(channelId: string, pinned: boolean): Promise<void> {
    if (!this.session(channelId)) return;
    this.store.dispatch(pinnedSet({ id: channelId, pinned }));
    await patchSession(channelId, { pinned });
  }

  /** Manually archive a session now (#163): drop its live subscription/socket but keep the card,
   *  transcript, and unread. The user can tap to reconnect later. No-op for cold/ephemeral sessions. */
  archive(channelId: string): void {
    this.coolDown(channelId);
  }

  /** True when a stored session should be auto-deleted (#163): not pinned, not the spared (active/
   *  last-active) session, and its *witnessed* silence — the app-observed gap between the last time
   *  we saw it (`lastSubscribedAt`) and its last real pulse (`lastHeartbeatAt`) — exceeds
   *  AUTO_DELETE_MS. Sessions we've never witnessed with a pulse yet (either clock missing) are never
   *  eligible, so a freshly-paired card is safe. */
  private isDeleteEligible(s: StoredSession, spareId: string | null): boolean {
    if (s.pinned) return false;
    if (s.pairing.channelId === spareId) return false;
    const beat = s.lastHeartbeatAt;
    const witnessed = s.lastSubscribedAt;
    if (beat == null || witnessed == null) return false;
    return witnessed - beat > AUTO_DELETE_MS;
  }

  /** #163 auto-delete sweep. Runs on every foreground. Purges any stored, non-pinned session whose
   *  witnessed silence exceeds AUTO_DELETE_MS (see {@link isDeleteEligible}). Silent, no undo. The
   *  currently-active session is always spared. */
  private async sweepExpired(): Promise<void> {
    const activeId = this.activeId();
    let stored: StoredSession[];
    try {
      stored = await loadSessions();
    } catch {
      return;
    }
    for (const s of stored) {
      if (this.isDeleteEligible(s, activeId)) {
        await this.remove(s.pairing.channelId);
      }
    }
  }

  /** True when a stored session should start (or be moved) in the calm **Archived** state (#163).
   *
   *  Two independent rules, either of which is enough:
   *
   *  1. *Witnessed silence* — the app-observed gap between the last time we looked (`lastSubscribedAt`)
   *     and the last real pulse (`lastHeartbeatAt`) is past AUTO_ARCHIVE_MS (6h) but not yet
   *     AUTO_DELETE_MS (2d, at which point {@link isDeleteEligible} purges it instead).
   *  2. *Both clocks stale* — each of the two clocks, measured against `now` rather than against each
   *     other, is older than AUTO_ARCHIVE_STALE_MS. This is the rule that catches a phone that was
   *     simply closed: witnessed silence can't accrue while nothing is running, so rule 1 never sees
   *     the gap, and the session comes back looking freshly-seen no matter how long it has been.
   *
   *  Pinned and the spared (active/last-active) session are never auto-archived, and a session we
   *  have never witnessed with a pulse (either clock missing) is ineligible under both rules — a
   *  freshly-paired card is safe. */
  private isArchiveEligible(s: StoredSession, spareId: string | null): boolean {
    if (s.pinned) return false;
    if (s.pairing.channelId === spareId) return false;
    const beat = s.lastHeartbeatAt;
    const witnessed = s.lastSubscribedAt;
    if (beat == null || witnessed == null) return false;
    const silence = witnessed - beat;
    if (silence > AUTO_ARCHIVE_MS && silence <= AUTO_DELETE_MS) return true;
    const now = this.clock();
    return now - beat > AUTO_ARCHIVE_STALE_MS && now - witnessed > AUTO_ARCHIVE_STALE_MS;
  }

  async reconnect(channelId: string): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    const ctrl = this.ensureController(channelId);
    if (ctrl.ephemeral || ctrl.reconnecting) return;
    ctrl.reconnecting = true;
    this.store.dispatch(statusSet({ id: channelId, status: 'connecting', error: undefined }));
    try {
      const stored = (await loadSessions()).find((s) => s.pairing.channelId === channelId);
      if (!stored) {
        // "No longer saved" is only true of a session that *was* saved. A spawn/resume placeholder
        // is never persisted (it has no pairing until the laptop answers), so telling the user their
        // session was deleted is both false and unactionable — it earned its own wording.
        const placeholder = session.meta.kind === 'spawning';
        this.store.dispatch(
          statusSet({
            id: channelId,
            status: 'error',
            error: placeholder
              ? 'This session never finished starting, so there is nothing to reconnect to. Try resuming it again.'
              : 'This session is no longer saved on this device.',
          }),
        );
        return;
      }
      const client = await connectSession(stored.pairing);
      this.attach(channelId, client);
    } catch (err) {
      this.store.dispatch(
        statusSet({ id: channelId, status: 'error', error: errMessage(err, 'Failed to reconnect.') }),
      );
    } finally {
      ctrl.reconnecting = false;
    }
  }

  /** Reconnect and wait to find out whether it actually took. {@link reconnect} only reports that a
   *  socket opened; the laptop confirms the session separately, and a `weft` that has gone away
   *  leaves the card sitting in `connecting` until the watchdog times it out. Resolves true once the
   *  session is genuinely back, false on error or if the confirmation never arrives. */
  private async reconnectAndConfirm(channelId: string): Promise<boolean> {
    await this.reconnect(channelId);
    const deadline = this.clock() + HOST_CONFIRM_MS;
    for (;;) {
      const status = this.session(channelId)?.connection.status;
      if (status === undefined || status === 'error' || status === 'ended') return false;
      if (status === 'live' || status === 'idle') return true;
      if (this.clock() >= deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, RECONNECT_POLL_MS));
    }
  }

  // --- user actions --------------------------------------------------------------------------------
  async sendPrompt(
    channelId: string,
    text: string,
    attachments?: PromptAttachment[],
    delivery: PromptDelivery = 'immediate',
  ): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    const wasBusy = session.connection.busy;
    const ts = this.clock();
    const item = makeUserItem(
      `user-${ts}-${Math.random().toString(36).slice(2, 7)}`,
      text,
      ts,
      attachments,
      delivery,
    );
    this.store.dispatch(userPromptAppended({ id: channelId, item }));
    // Immediate prompts start or steer the active turn, so reflect working state right away.
    // Enqueued prompts leave the current turn's state untouched.
    if (delivery === 'immediate') {
      this.store.dispatch(busySet({ id: channelId, busy: true, ts }));
    }
    this.schedulePersist(channelId);
    this.markActivity(channelId);
    try {
      await this.deliverPrompt(channelId, text, attachments, delivery);
    } catch {
      const current = this.session(channelId);
      if (!current) return;
      // A failed first prompt returns to idle; a failed steering message must not make the active
      // turn look idle while Copilot is still working. If the host reported idle while this send
      // was in flight, preserve that newer authoritative state instead of resurrecting busy.
      if (delivery === 'immediate' && current.connection.busy) {
        this.store.dispatch(busySet({ id: channelId, busy: wasBusy, ts: this.clock() }));
      }
      this.store.dispatch(promptFailed({ id: channelId, itemId: item.id, failed: true }));
      this.schedulePersist(channelId);
    }
  }

  async retryPrompt(channelId: string, itemId: string): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    const item = session.transcript.items.find((i) => i.kind === 'user' && i.id === itemId);
    if (!item || item.kind !== 'user') return;
    this.store.dispatch(promptFailed({ id: channelId, itemId, failed: false }));
    this.schedulePersist(channelId);
    try {
      await this.deliverPrompt(channelId, item.text, item.attachments, item.delivery);
    } catch {
      if (!this.session(channelId)) return;
      this.store.dispatch(promptFailed({ id: channelId, itemId, failed: true }));
      this.schedulePersist(channelId);
    }
  }

  private async deliverPrompt(
    channelId: string,
    text: string,
    attachments?: PromptAttachment[],
    delivery: PromptDelivery = 'immediate',
  ): Promise<void> {
    await this.send(channelId, prompt(text, attachments, delivery));
  }

  async sendApproval(channelId: string, requestId: string, optionId: string): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    // Ignore a second tap while the first decision is still sending (#88); tracking the requestId
    // also lets a racing state snapshot drop the already-dismissed card (#79).
    if (this.inFlightDecisions.has(requestId)) return;
    const pending = session.requests.approvals.find((a) => a.requestId === requestId);
    this.inFlightDecisions.add(requestId);
    try {
      await this.store.dispatch(
        optimistic({
          apply: approvalDismissed({ id: channelId, requestId }),
          send: () => this.send(channelId, approvalDecision(requestId, optionId)),
          rollback: (err) =>
            pending
              ? approvalRestored({
                  id: channelId,
                  req: pending,
                  error: errMessage(err, 'Could not send your decision — tap again to retry.'),
                })
              : undefined,
        }),
      );
    } finally {
      this.inFlightDecisions.delete(requestId);
    }
  }

  async sendElicitation(
    channelId: string,
    requestId: string,
    action: 'accept' | 'decline' | 'cancel',
    content?: Record<string, string | number | boolean | string[]>,
  ): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    // Ignore a second submit while the first answer is still sending (#88); tracking the requestId
    // also lets a racing state snapshot drop the already-dismissed card (#79).
    if (this.inFlightDecisions.has(requestId)) return;
    const pending = session.requests.elicitations.find((e) => e.requestId === requestId);
    this.inFlightDecisions.add(requestId);
    try {
      await this.store.dispatch(
        optimistic({
          apply: elicitationDismissed({ id: channelId, requestId }),
          send: () => this.send(channelId, elicitationResponse(requestId, action, content)),
          rollback: (err) =>
            pending
              ? elicitationRestored({
                  id: channelId,
                  req: pending,
                  error: errMessage(err, 'Could not send your answer — try again.'),
                })
              : undefined,
        }),
      );
    } finally {
      this.inFlightDecisions.delete(requestId);
    }
  }

  async sendInterrupt(channelId: string): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    // Release the Stop affordance immediately and settle any still-"running" tool so the button
    // can't wedge on a slow/dead host (#77). The host's own ACTIVITY(false)/TOOL_COMPLETE remains
    // authoritative and overrides this if the turn is genuinely still in flight.
    this.store.dispatch(interruptRequested({ id: channelId, ts: this.clock() }));
    try {
      await this.send(channelId, interrupt());
    } catch (err) {
      const latest = this.session(channelId);
      if (latest) {
        this.store.dispatch(
          statusSet({
            id: channelId,
            status: latest.connection.status,
            error: errMessage(err, 'Couldn’t send Stop — tap to retry.'),
          }),
        );
      }
    }
  }

  async sendMode(channelId: string, mode: SessionMode): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    const prev = session.connection.mode;
    this.store.dispatch(modeSet({ id: channelId, mode, pending: true }));
    try {
      await this.send(channelId, modeChange(mode));
    } catch {
      this.store.dispatch(modeSet({ id: channelId, mode: prev }));
      this.store.dispatch(
        noticeAppended({
          id: channelId,
          level: 'warning',
          text: `Couldn't switch to ${mode} — still in ${prev}.`,
          ts: this.clock(),
        }),
      );
      this.schedulePersist(channelId);
    }
  }

  async sendCommand(channelId: string, name: string, input?: string): Promise<void> {
    const session = this.session(channelId);
    if (!session) return;
    const shown = input ? `/${name} ${input}` : `/${name}`;
    try {
      await this.send(channelId, invokeCommand(name, input));
    } catch {
      this.store.dispatch(
        noticeAppended({
          id: channelId,
          level: 'warning',
          text: `Couldn't run ${shown} — tap to retry.`,
          ts: this.clock(),
        }),
      );
      this.schedulePersist(channelId);
    }
  }

  async setVoiceMode(active: boolean, channelId = this.activeId()): Promise<void> {
    if (!channelId) return;
    const session = this.session(channelId);
    const ctrl = this.controllers.get(channelId);
    if (!session || session.meta.kind !== 'live' || session.connection.status !== 'live' || !ctrl?.client) {
      return;
    }
    try {
      await this.send(channelId, voiceMode(active));
    } catch {
      // Voice mode is an advisory hint for the extension; never block or surface UI errors for it.
    }
  }

  // --- outbound funnel + persistence ---------------------------------------------------------------
  private async send(channelId: string, message: EventEnvelope): Promise<void> {
    const client = this.registry.get(channelId);
    if (!client) throw new Error('No active connection.');
    this.recordEvent(channelId, 'out', message);
    await client.send(message);
  }

  private recordEvent(channelId: string, dir: 'in' | 'out', message: EventEnvelope): void {
    if (isTerminalEnvelope(message)) return;
    // Heartbeats fire ~every 2.5s; the reducer collapses a run of consecutive heartbeats down to
    // the latest one (#67, #185) so they surface liveness in the log without evicting the
    // substantive event chain (prompts, approvals, tool_start/complete, elicitations).
    const fallback = dir === 'out' ? getSenderName() : 'Copilot';
    const event = toDebugEvent(dir, message, (this.eventSeq += 1), fallback);
    this.store.dispatch(debugAppended({ id: channelId, event }));
    this.scheduleEventPersist(channelId);
  }

  /** Mirrors recordEvent() but for the DEVICE (listener) channel — same shape, its own (smaller,
   *  now-persisted) ring buffer, and the same consecutive-heartbeat collapsing (#185). */
  private recordDeviceEvent(channelId: string, dir: 'in' | 'out', message: EventEnvelope): void {
    if (isTerminalEnvelope(message)) return;
    if ([...this.demoDevices.values()].includes(channelId)) return;
    const fallback = dir === 'out' ? getSenderName() : (this.device(channelId)?.name ?? 'Listener');
    const event = toDebugEvent(dir, message, (this.eventSeq += 1), fallback);
    this.store.dispatch(deviceEventAppended({ channelId, event }));
    this.scheduleDeviceEventPersist(channelId);
  }

  private schedulePersist(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral || ctrl.has('save')) return;
    ctrl.arm(
      'save',
      () => {
        ctrl.clear('save');
        const s = this.session(channelId);
        if (s) void saveTranscript(channelId, toPersisted(toTimelineState(s)));
      },
      PERSIST_THROTTLE_MS,
    );
  }

  private scheduleMetaPersist(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral || ctrl.has('meta')) return;
    ctrl.arm(
      'meta',
      () => {
        ctrl.clear('meta');
        const s = this.session(channelId);
        if (s) void patchSession(channelId, { lastEventAt: s.lastEventAt ?? null, unread: s.unread ?? false, unreadCount: s.unreadCount ?? 0 });
      },
      META_PERSIST_THROTTLE_MS,
    );
  }

  private scheduleEventPersist(channelId: string): void {
    const ctrl = this.controllers.get(channelId);
    if (!ctrl || ctrl.ephemeral || ctrl.has('eventSave')) return;
    ctrl.arm(
      'eventSave',
      () => {
        ctrl.clear('eventSave');
        const s = this.session(channelId);
        if (s) void saveEventLog(channelId, s.debug);
      },
      PERSIST_THROTTLE_MS,
    );
  }

  /** Mirrors scheduleEventPersist() but for a listener/device channel. listenerControllers are
   *  always `ephemeral: true` (that flag only means "not a real session" here, not "don't
   *  persist") so, unlike the session persist helpers, this does NOT gate on ctrl.ephemeral. */
  private scheduleDeviceEventPersist(channelId: string): void {
    const ctrl = this.ensureListenerController(channelId);
    if (ctrl.has('deviceEventSave')) return;
    ctrl.arm(
      'deviceEventSave',
      () => {
        ctrl.clear('deviceEventSave');
        const d = this.device(channelId);
        if (d) void saveEventLog(channelId, d.events);
      },
      PERSIST_THROTTLE_MS,
    );
  }

  /** Persist the two liveness clocks (#163): `lastHeartbeatAt` (last observed pulse) and
   *  `lastSubscribedAt` (= now, the moment the running app last witnessed this session). Throttled
   *  to a coarse window so a per-second watchdog tick can't thrash storage; pass `flush` to write
   *  the exact edge synchronously (subscribe-success / coolDown / pagehide) regardless of throttle.
   *  Controller-INDEPENDENT on purpose: it runs for cold/archived sessions too, so the witnessed
   *  clock keeps advancing while the app is foreground. Because the watchdog only ticks while the
   *  app is alive, phone-off time never advances it — that's what makes the delete clock count only
   *  *witnessed* silence, so a still-alive laptop is never auto-deleted. */
  private persistLiveness(channelId: string, flush = false): void {
    const ctrl = this.controllers.get(channelId);
    if (ctrl?.ephemeral) return; // never persist ephemeral (unstored) sessions
    const now = this.clock();
    if (!flush) {
      const last = this.lastLivenessWriteAt.get(channelId) ?? 0;
      if (now - last < LIVENESS_PERSIST_THROTTLE_MS) return;
    }
    const s = this.session(channelId);
    if (!s) return;
    this.lastLivenessWriteAt.set(channelId, now);
    // Only rewrite lastHeartbeatAt when we actually hold a fresh in-memory pulse. A cold/archived
    // session has no live beat (connection.lastHeartbeat is null after load) — writing null would
    // erase the real last-beat clock from a prior run and break the witnessed-silence math. In that
    // case advance only lastSubscribedAt; the stored lastHeartbeatAt stays put.
    const patch: Parameters<typeof patchSession>[1] = { lastSubscribedAt: now };
    if (s.connection.lastHeartbeat) {
      patch.lastHeartbeatAt = s.connection.lastHeartbeat;
      this.lastKnownHeartbeat.set(channelId, s.connection.lastHeartbeat);
    }
    void patchSession(channelId, patch);
  }

  // --- watchdog + resume ---------------------------------------------------------------------------
  private startWatchdog(): void {
    if (this.watchdog !== null) return;
    this.watchdog = setInterval(() => {
      const now = this.clock();
      const activeId = this.activeId();
      for (const session of selectAllSessions(this.store.getState())) {
        const ctrl = this.controllers.get(session.id);
        if (!ctrl || ctrl.ephemeral) continue;
        // Advance the witness clock for every stored session on every pass, cold ones included.
        // Without this the only writers are subscribe-success, coolDown and pagehide, so a session
        // that can never subscribe never moves `lastSubscribedAt` — its witnessed silence stays
        // pinned near zero and neither the archive nor the delete window can ever be reached. That
        // is how a card with a twelve-day-old last message sits in Offline forever. The write is
        // throttled internally, so a per-second tick costs nothing.
        this.persistLiveness(session.id);
        const status = session.connection.status;
        if (status === 'connecting') {
          if (ctrl.connectingSince == null) {
            ctrl.connectingSince = now;
          } else if (now - ctrl.connectingSince > HOST_CONFIRM_MS) {
            this.store.dispatch(
              statusSet({
                id: session.id,
                status: 'error',
                error: 'Couldn’t reach your session — the terminal may be closed. Tap Reconnect to try again.',
              }),
            );
            if (session.connection.busy) this.store.dispatch(busySet({ id: session.id, busy: false }));
            ctrl.connectingSince = null;
            ctrl.reconnecting = false;
            this.clearSettle(session.id);
          }
          continue;
        }
        ctrl.connectingSince = null;
        const beat = session.connection.lastHeartbeat;
        if (beat != null) this.lastKnownHeartbeat.set(session.id, beat);
        // The pulse to judge staleness by. `connection.lastHeartbeat` is in-memory only and is null
        // for every card restored from disk, so gating on it alone left a session that never manages
        // to reconnect stuck: connecting → timeout → Offline, and never archived, because there was
        // no beat to measure from. Fall back to the last pulse we ever saw, which outlives reloads.
        const lastBeat = beat ?? this.lastKnownHeartbeat.get(session.id) ?? null;
        // Auto-archive (#163): once we've heard nothing from the laptop for AUTO_ARCHIVE_MS, drop
        // to the calm Archived state (cold, socket released, out of the warm pool) regardless of
        // whether the card is still Live/Quiet or already flipped to Offline. Runs before the
        // live/idle guard so an Offline (error) card can still cool down. Never archive the card the
        // user is viewing, a busy turn, or one already cold/ended.
        if (
          lastBeat != null &&
          now - lastBeat > AUTO_ARCHIVE_MS &&
          !session.connection.cold &&
          !session.connection.busy &&
          session.connection.status !== 'ended' &&
          session.id !== activeId
        ) {
          this.coolDown(session.id);
          continue;
        }
        const heartbeatStale = beat != null && now - beat > OFFLINE_AFTER_MS;
        if ((status === 'live' || status === 'idle') && heartbeatStale) {
          this.store.dispatch(
            statusSet({ id: session.id, status: 'error', error: 'Connection lost — reconnect to resume.' }),
          );
          if (session.connection.busy) this.store.dispatch(busySet({ id: session.id, busy: false }));
        } else if (status === 'live' && beat && now - beat > IDLE_AFTER_MS) {
          this.store.dispatch(statusSet({ id: session.id, status: 'idle', error: undefined }));
          if (session.connection.busy) this.store.dispatch(busySet({ id: session.id, busy: false }));
        }
        const needsReconnect = heartbeatStale || status === 'error' || !ctrl.client;
        const lastTry = this.sessionReconnectAt.get(session.id) ?? 0;
        const failures = this.sessionReconnectFails.get(session.id) ?? 0;
        const backoff = lastTry === 0 ? 0 : sessionReconnectBackoffMs(failures);
        if (
          needsReconnect &&
          !ctrl.reconnecting &&
          !session.connection.cold &&
          session.connection.status !== 'ended' &&
          now - lastTry >= backoff
        ) {
          this.sessionReconnectAt.set(session.id, now);
          this.sessionReconnectFails.set(session.id, failures + 1);
          void this.reconnect(session.id);
        }
      }
      // Device (listener) channels: flip the Online dot to Offline once its heartbeat/lastSeenAt
      // goes stale, rather than trusting the transport's own connect state alone (a hung/crashed
      // `weft` process can leave the socket looking "connected" — see DEVICE_HEARTBEAT).
      for (const device of this.store.getState().sessions.devices) {
        if (device.connected && device.lastSeenAt && now - device.lastSeenAt > DEVICE_OFFLINE_AFTER_MS) {
          this.clearDeviceUtilities(device.channelId);
          this.store.dispatch(deviceErrorSet({ channelId: device.channelId, connected: false }));
          this.terminals.get(device.channelId)?.setConnected(false);
        }
        // Self-heal a wedged device without waiting for an app foreground (handleResume) or restart
        // (init): the devtunnel/relay socket never re-opens on its own (unlike supabase), so once it
        // drops nothing re-establishes it unless we drive it here. Retry on a backoff whenever the
        // device is offline or its listener client is missing/dead.
        const ctrl = this.listenerController(device.channelId);
        const healthy = device.connected && !!ctrl?.client;
        const lastTry = this.deviceReconnectAt.get(device.channelId) ?? 0;
        const fails = this.deviceReconnectFails.get(device.channelId) ?? 0;
        const sinceSeen = device.lastSeenAt != null ? now - device.lastSeenAt : Number.POSITIVE_INFINITY;
        const backoff = deviceReconnectBackoffMs(fails, sinceSeen);
        if (!healthy && !ctrl?.reconnecting && now - lastTry > backoff) {
          this.deviceReconnectAt.set(device.channelId, now);
          this.deviceReconnectFails.set(device.channelId, fails + 1);
          void this.connectDevice(device.channelId);
        } else if (healthy && (lastTry !== 0 || fails !== 0)) {
          this.deviceReconnectAt.delete(device.channelId);
          this.deviceReconnectFails.delete(device.channelId);
        }
      }
    }, 1_000);
  }

  private handleResume = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const now = this.clock();
    // Foregrounding fires visibilitychange AND Capacitor appStateChange (and sometimes `online`)
    // back-to-back; a short leading-edge debounce collapses the overlapping triggers into one pass
    // so each warm session issues at most one state+history sync (#75).
    if (now - this.lastResumeAt < RESUME_DEBOUNCE_MS) return;
    this.lastResumeAt = now;
    void this.sweepExpired(); // #163: purge witnessed-silence-expired sessions on each foreground
    for (const channelId of [...this.warmLru]) {
      const ctrl = this.controllers.get(channelId);
      const session = this.session(channelId);
      if (!ctrl || !session || ctrl.ephemeral) continue;
      // A session still inside its 30s confirmation window (just created a client) must not be torn
      // down and reconnected — that closes the fresh client, drops a pending host reply, and resets
      // the clock (#125). Let the confirm gate / watchdog resolve it.
      if (session.connection.status === 'connecting') continue;
      const beat = session.connection.lastHeartbeat;
      const stale = beat != null && now - beat > IDLE_AFTER_MS;
      const revive = !ctrl.client || session.connection.status === 'error' || stale;
      if (revive && session.connection.status !== 'ended') {
        void this.reconnect(channelId);
      } else {
        this.requestState(channelId);
        if (ctrl.client) this.syncHistory(channelId, ctrl.client);
      }
    }
    // Devices: mirror the session revive check above. Every registered device is always "warm" (no
    // LRU, capped at MAX_DEVICES), so just revive any whose listener client is missing/stale.
    for (const device of this.store.getState().sessions.devices) {
      const ctrl = this.listenerController(device.channelId);
      const stale = device.lastSeenAt != null && now - device.lastSeenAt > DEVICE_OFFLINE_AFTER_MS;
      if (!ctrl?.client || !device.connected || stale) {
        // Foregrounding resets the backoff ramp: clear the attempt counter + throttle stamp so the
        // returning user gets an immediate attempt-0 reconnect, however relaxed the background ramp
        // had become, then the watchdog re-ramps from scratch if this attempt also fails.
        this.deviceReconnectFails.delete(device.channelId);
        this.deviceReconnectAt.delete(device.channelId);
        void this.connectDevice(device.channelId);
      } else {
        this.terminals.get(device.channelId)?.reattach();
        if (device.capabilities?.includes(DEVICE_CAPABILITY.KEEP_AWAKE_V1)) {
          this.refreshDeviceKeepAwake(device.channelId);
        }
      }
    }
  };

  /** #163: on background/hide, flush the witness clock for every session we currently hold warm so
   *  `lastSubscribedAt` records the exact moment we stopped watching (the throttled per-tick writes
   *  may lag by up to LIVENESS_PERSIST_THROTTLE_MS). This keeps witnessed-silence honest across an
   *  app suspend, without waiting for the next foreground tick. */
  private handleHide = (event?: Event): void => {
    if (event?.type !== 'visibilitychange' || document.visibilityState === 'hidden') {
      for (const device of this.store.getState().sessions.devices) this.closeDeviceClipboard(device.channelId);
    }
    for (const channelId of [...this.warmLru]) {
      this.persistLiveness(channelId, /* flush */ true);
    }
  };

  private installResumeTriggers(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', this.handleResume);
    this.resumeCleanups.push(() => window.removeEventListener('online', this.handleResume));
    window.addEventListener('pagehide', this.handleHide);
    this.resumeCleanups.push(() => window.removeEventListener('pagehide', this.handleHide));
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleResume);
      this.resumeCleanups.push(() => document.removeEventListener('visibilitychange', this.handleResume));
      document.addEventListener('visibilitychange', this.handleHide);
      this.resumeCleanups.push(() => document.removeEventListener('visibilitychange', this.handleHide));
    }
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) this.handleResume();
      else {
        for (const device of this.store.getState().sessions.devices) this.closeDeviceClipboard(device.channelId);
        this.handleHide();
      }
    })
      .then((handle) => {
        this.resumeCleanups.push(() => handle.remove());
      })
      .catch(() => {
        // No Capacitor App bridge here — the visibility/online triggers already cover the web path.
      });
  }

  /** Tear down every socket, timer, and listener. Called when the app (or a test harness) shuts down. */
  dispose(): void {
    for (const waiters of this.lifecycleWaiters.values()) {
      for (const waiter of waiters) {
        clearTimeout(waiter.timer);
        waiter.reject(new Error('Session runtime disposed.'));
      }
    }
    this.lifecycleWaiters.clear();
    for (const terminal of this.terminals.values()) terminal.dispose();
    this.terminals.clear();
    for (const device of this.store.getState().sessions.devices) this.clearDeviceUtilities(device.channelId);
    if (this.watchdog !== null) {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }
    for (const cleanup of this.resumeCleanups) {
      try {
        cleanup();
      } catch {
        /* ignore */
      }
    }
    this.resumeCleanups = [];
    for (const ctrl of this.controllers.values()) ctrl.dispose();
    this.controllers.clear();
    for (const ctrl of this.listenerControllers.values()) ctrl.dispose();
    this.listenerControllers.clear();
    for (const lease of this.deviceMonitors.values()) clearInterval(lease.renewal);
    this.deviceMonitors.clear();
    this.deviceHealthCacheWrittenAt.clear();
    for (const pending of this.pendingSpawns.values()) clearSpawnTimers(pending);
    this.pendingSpawns.clear();
    this.registry.disposeAll();
  }
}

export function createSessionRuntime(store?: AppStore): SessionRuntime {
  return new SessionRuntime(store);
}

import { ChannelController } from './channelController';
